import React, { useState, useEffect, useCallback } from 'react';
import { 
  Users, Ticket, BarChart3, Activity, Mail, Clock, 
  CheckCircle, AlertCircle, XCircle, ChevronDown, ChevronUp,
  Eye, Trash2, MessageSquare, Filter, RefreshCw, Search,
  TrendingUp, UserCheck, Calendar, Shield, DollarSign, Key, Copy, EyeOff, Crown,
  Plus, X, UserPlus, UsersRound, Edit2, Hash, Package, LayoutGrid,
  ToggleLeft, ToggleRight, Sparkles, Building2, UtensilsCrossed, ShoppingCart,
  Clapperboard, Share2, Target, Boxes, Plane, Save, ChevronRight, Lock, Check, ExternalLink, FileText, List
} from 'lucide-react';
import { toast } from 'sonner';

// Use runtime URL detection to ensure correct API endpoint
const getApiUrlRuntime = () => {
  const currentOrigin = window.location.origin;
  const envUrl = process.env.REACT_APP_BACKEND_URL;
  
  // If we're on a custom domain (not preview.emergentagent.com), use the current origin
  if (!currentOrigin.includes('preview.emergentagent.com')) {
    return currentOrigin;
  }
  
  // For preview environments, use current origin to ensure correct routing
  return currentOrigin;
};

const API_URL = getApiUrlRuntime();
const ADMIN_KEY = 'creatrhq-admin-2026';

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [analytics, setAnalytics] = useState(null);
  const [users, setUsers] = useState([]);
  const [messagingMembers, setMessagingMembers] = useState([]);
  const [messagingLoading, setMessagingLoading] = useState(false);

  // Teams state
  const [teams, setTeams] = useState([]);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [showCreateTeam, setShowCreateTeam] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [newTeamName, setNewTeamName] = useState('');
  const [newTeamDesc, setNewTeamDesc] = useState('');
  const [newTeamColor, setNewTeamColor] = useState('purple');
  const [newTeamMembers, setNewTeamMembers] = useState([]);
  const [teamSubTab, setTeamSubTab] = useState('teams'); // 'teams' | 'members'
  const [tickets, setTickets] = useState([]);
  const [ticketCounts, setTicketCounts] = useState({});
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ticketFilter, setTicketFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [showPasswords, setShowPasswords] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [togglingAdmin, setTogglingAdmin] = useState(null);
  
  // Inline editing state for user fields
  const [editingCell, setEditingCell] = useState(null); // { index: number, field: string }
  const [editValue, setEditValue] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  
  // Add User Modal state
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  // Sort config for the Users table — click any column header to cycle asc/desc/none
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
  // Invoices modal state — shows all purchase records for one user
  const [invoicesModalUser, setInvoicesModalUser] = useState(null);
  const [invoicesData, setInvoicesData] = useState(null);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  // Edit User Modal state — opens full form for ALL user fields
  const [editingUser, setEditingUser] = useState(null);   // The user object being edited
  const [editUserForm, setEditUserForm] = useState({});   // Form state
  const [savingFullEdit, setSavingFullEdit] = useState(false);
  const [newUserForm, setNewUserForm] = useState({
    name: '',
    email: '',
    password: '',
    membership_plan: 'free',
    total_paid: 0,
    role: '',
    industry_template_name: '',
    is_admin: false
  });
  const [creatingUser, setCreatingUser] = useState(false);

  // Org Teams state (multi-user for Small Business / Enterprise)
  const [orgTeams, setOrgTeams] = useState([]);
  const [orgTeamsLoading, setOrgTeamsLoading] = useState(false);
  const [expandedTeam, setExpandedTeam] = useState(null);
  const [newMemberEmail, setNewMemberEmail] = useState({});
  const [addingMember, setAddingMember] = useState(null);
  const [showNewTeamModal, setShowNewTeamModal] = useState(false);
  const [newTeamForm, setNewTeamForm] = useState({ name: '', owner_email: '', plan_tier: 'smallbiz' });
  const [creatingTeam, setCreatingTeam] = useState(false);

  // Add-ons & Templates state
  const [addonPages, setAddonPages] = useState([]);
  const [addonsLoading, setAddonsLoading] = useState(false);
  const [editingAddon, setEditingAddon] = useState(null);
  const [industryTemplates, setIndustryTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showNewTemplate, setShowNewTemplate] = useState(false);
  const [newTemplate, setNewTemplate] = useState({ name: '', description: '', icon: 'Briefcase', color: '#6B7280', category: 'Small Business', core_pages: [], addon_pages: [] });
  const [showNewAddon, setShowNewAddon] = useState(false);
  const [newAddon, setNewAddon] = useState({ page_key: '', label: '', route: '', description: '', price_monthly: 4.99, is_addon: true, icon: 'Package', color: '#3B82F6', category: 'Lifestyle' });

  // Toggle admin role for a user
  const toggleAdminRole = async (user) => {
    const userId = user.user_id || user.email;
    setTogglingAdmin(userId);
    try {
      const response = await fetch(
        `${API_URL}/api/admin/users/${encodeURIComponent(userId)}/admin-role?admin_key=${ADMIN_KEY}`,
        { method: 'PUT' }
      );
      const data = await response.json();
      if (data.success) {
        // Update local state
        setUsers(prev => prev.map(u => 
          (u.user_id === userId || u.email === userId) 
            ? { ...u, is_admin: data.is_admin } 
            : u
        ));
        toast.success(data.message);
      } else {
        toast.error(data.detail || 'Failed to update admin role');
      }
    } catch (error) {
      console.error('Error toggling admin role:', error);
      toast.error('Failed to update admin role');
    } finally {
      setTogglingAdmin(null);
    }
  };

  // Save inline edit for user field
  const saveInlineEdit = async (user, field, value) => {
    const userId = user.user_id || user.email;
    setSavingEdit(true);
    
    try {
      // Build the update payload
      const updatePayload = {};
      
      // Handle different field types
      if (field === 'name') {
        updatePayload.name = value;
      } else if (field === 'email') {
        updatePayload.email = value;
      } else if (field === 'membership_plan') {
        updatePayload.membership_plan = value;
      } else if (field === 'total_paid') {
        updatePayload.total_paid = parseFloat(value) || 0;
      } else if (field === 'role') {
        updatePayload.role = value;
      } else if (field === 'industry_template_name') {
        updatePayload.industry_template_name = value;
      }
      
      const response = await fetch(
        `${API_URL}/api/admin/users/${encodeURIComponent(userId)}?admin_key=${ADMIN_KEY}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(updatePayload)
        }
      );
      
      const data = await response.json();
      
      if (response.ok && data.success !== false) {
        // Update local state
        setUsers(prev => prev.map(u => 
          (u.user_id === userId || u.email === userId) 
            ? { ...u, [field]: field === 'total_paid' ? parseFloat(value) || 0 : value } 
            : u
        ));
        toast.success(`Updated ${field} successfully`);
        setEditingCell(null);
        setEditValue('');
      } else {
        toast.error(data.detail || `Failed to update ${field}`);
      }
    } catch (error) {
      console.error('Error saving inline edit:', error);
      toast.error(`Failed to update ${field}`);
    } finally {
      setSavingEdit(false);
    }
  };

  // Start inline edit
  const startInlineEdit = (idx, field, currentValue) => {
    setEditingCell({ index: idx, field });
    setEditValue(currentValue || '');
  };

  // Cancel inline edit
  const cancelInlineEdit = () => {
    setEditingCell(null);
    setEditValue('');
  };

  // Click a column header to sort the users table: asc → desc → none
  const handleSort = (key) => {
    setSortConfig(prev => {
      if (prev.key !== key) return { key, direction: 'asc' };
      if (prev.direction === 'asc') return { key, direction: 'desc' };
      return { key: null, direction: null }; // third click clears sort
    });
  };

  // Derived sorted users list
  const sortedUsers = React.useMemo(() => {
    if (!sortConfig.key || !sortConfig.direction) return users;
    const key = sortConfig.key;
    const dir = sortConfig.direction === 'asc' ? 1 : -1;
    const cmp = (a, b) => {
      const av = a?.[key];
      const bv = b?.[key];
      // Booleans: false < true
      if (typeof av === 'boolean' || typeof bv === 'boolean') {
        return ((av ? 1 : 0) - (bv ? 1 : 0)) * dir;
      }
      // Numbers
      if (typeof av === 'number' && typeof bv === 'number') {
        return (av - bv) * dir;
      }
      // Dates (last_login)
      if (key === 'last_login') {
        const da = av ? new Date(av).getTime() : 0;
        const db = bv ? new Date(bv).getTime() : 0;
        return (da - db) * dir;
      }
      // Strings — case-insensitive, empties last
      const sa = (av == null ? '' : String(av)).toLowerCase();
      const sb = (bv == null ? '' : String(bv)).toLowerCase();
      if (!sa && sb) return 1;
      if (sa && !sb) return -1;
      return sa.localeCompare(sb) * dir;
    };
    return [...users].sort(cmp);
  }, [users, sortConfig]);

  // Render a sort indicator next to the column label
  const SortIndicator = ({ columnKey }) => {
    const active = sortConfig.key === columnKey;
    return (
      <span className="inline-flex flex-col -my-1 ml-1 align-middle leading-none">
        <ChevronUp
          size={9}
          className={active && sortConfig.direction === 'asc' ? 'text-white' : 'text-gray-600'}
        />
        <ChevronDown
          size={9}
          className={active && sortConfig.direction === 'desc' ? 'text-white' : 'text-gray-600'}
        />
      </span>
    );
  };

  // Open the Invoices modal for a user — fetches their full purchase history.
  const openInvoicesModal = async (user) => {
    setInvoicesModalUser(user);
    setInvoicesData(null);
    setInvoicesLoading(true);
    try {
      const userId = user.user_id || user.email;
      const response = await fetch(
        `${API_URL}/api/admin/users/${encodeURIComponent(userId)}/invoices?admin_key=${ADMIN_KEY}`
      );
      const data = await response.json();
      if (response.ok) {
        setInvoicesData(data);
      } else {
        toast.error(data.detail || 'Failed to load invoices');
        setInvoicesModalUser(null);
      }
    } catch (e) {
      console.error(e);
      toast.error('Failed to load invoices');
      setInvoicesModalUser(null);
    } finally {
      setInvoicesLoading(false);
    }
  };

  // Open the full Edit User modal pre-filled with this user's current details
  const openEditUserModal = (user) => {
    setEditingUser(user);
    // Convert last_login ISO string to "YYYY-MM-DDTHH:mm" format for datetime-local input
    let lastLoginLocal = '';
    if (user.last_login) {
      const d = new Date(user.last_login);
      if (!isNaN(d.getTime())) {
        const pad = (n) => String(n).padStart(2, '0');
        lastLoginLocal = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
      }
    }
    setEditUserForm({
      name: user.name || '',
      email: user.email || '',
      password: '', // leave blank — only updated if admin types a new one
      phone: user.phone || '',
      company: user.company || '',
      role: user.role || '',
      industry_template_name: user.industry_template_name || '',
      membership_plan: user.membership_plan || 'free',
      total_paid: user.total_paid != null ? String(user.total_paid) : '0',
      setup_fee: user.setup_fee != null ? String(user.setup_fee) : '0',
      last_login: lastLoginLocal,
      invoice_file: user.invoice_file || '',
      invoice_filename: user.invoice_filename || '',
      invoice_filetype: user.invoice_filetype || '',
    });
  };

  // Save the full edit modal — sends every changed field in one PUT
  const saveFullUserEdit = async () => {
    if (!editingUser) return;
    const userId = editingUser.user_id || editingUser.email;
    setSavingFullEdit(true);
    try {
      const payload = {
        name: editUserForm.name,
        email: editUserForm.email,
        phone: editUserForm.phone,
        company: editUserForm.company,
        role: editUserForm.role,
        industry_template_name: editUserForm.industry_template_name,
        membership_plan: editUserForm.membership_plan,
        total_paid: parseFloat(editUserForm.total_paid) || 0,
        setup_fee: parseFloat(editUserForm.setup_fee) || 0,
        invoice_file: editUserForm.invoice_file || '',
        invoice_filename: editUserForm.invoice_filename || '',
        invoice_filetype: editUserForm.invoice_filetype || '',
      };
      // Convert datetime-local back to ISO string (preserve local tz offset)
      if (editUserForm.last_login) {
        const d = new Date(editUserForm.last_login);
        if (!isNaN(d.getTime())) payload.last_login = d.toISOString();
      } else if (editingUser.last_login) {
        // Admin explicitly cleared the field
        payload.last_login = null;
      }
      // Password is only sent when admin actually typed a new value.
      if (editUserForm.password && editUserForm.password.trim()) {
        payload.password = editUserForm.password.trim();
      }

      const response = await fetch(
        `${API_URL}/api/admin/users/${encodeURIComponent(userId)}?admin_key=${ADMIN_KEY}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      );
      const data = await response.json();
      if (response.ok && data.success !== false) {
        setUsers(prev => prev.map(u =>
          (u.user_id === userId || u.email === editingUser.email)
            ? {
                ...u,
                ...payload,
                total_paid: payload.total_paid,
                // If password was changed, keep the new plaintext locally so the
                // table's password cell updates instantly.
                ...(payload.password ? { plain_password: payload.password, has_password: true } : {}),
              }
            : u
        ));
        toast.success('User updated');
        setEditingUser(null);
      } else {
        toast.error(data.detail || 'Failed to update user');
      }
    } catch (e) {
      console.error(e);
      toast.error('Failed to update user');
    } finally {
      setSavingFullEdit(false);
    }
  };

  // Handle key press in inline edit
  const handleEditKeyPress = (e, user, field) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveInlineEdit(user, field, editValue);
    } else if (e.key === 'Escape') {
      cancelInlineEdit();
    }
  };

  // Create mock user
  const createMockUser = async () => {
    if (!newUserForm.name.trim() || !newUserForm.email.trim()) {
      toast.error('Name and email are required');
      return;
    }
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newUserForm.email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    setCreatingUser(true);
    try {
      const response = await fetch(
        `${API_URL}/api/admin/users?admin_key=${ADMIN_KEY}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(newUserForm)
        }
      );
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        toast.success(`Created mock user: ${data.email}`);
        // Add to local state
        setUsers(prev => [{
          user_id: data.user_id,
          ...newUserForm,
          is_mock_user: true,
          created_at: new Date().toISOString()
        }, ...prev]);
        // Reset form and close modal
        setNewUserForm({
          name: '',
          email: '',
          password: '',
          membership_plan: 'free',
          total_paid: 0,
          role: '',
          industry_template_name: '',
          is_admin: false
        });
        setShowAddUserModal(false);
      } else {
        toast.error(data.detail || 'Failed to create user');
      }
    } catch (error) {
      console.error('Error creating mock user:', error);
      toast.error('Failed to create user');
    } finally {
      setCreatingUser(false);
    }
  };

  // Fetch all admin data
  useEffect(() => {
    fetchAnalytics();
    fetchUsers();
    fetchTickets();
  }, []);

  useEffect(() => {
    if (activeTab === 'team-chat') { fetchMessagingMembers(); fetchTeams(); }
    if (activeTab === 'addons') { fetchAddonPages(); }
    if (activeTab === 'templates') { fetchIndustryTemplates(); }
    if (activeTab === 'teams') { fetchAllTeams(); }
  }, [activeTab]);

  const fetchAllTeams = async () => {
    setOrgTeamsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/teams?admin_key=${ADMIN_KEY}`);
      const data = await res.json();
      setOrgTeams(data.teams || []);
    } catch (e) { toast.error('Failed to load teams'); }
    finally { setOrgTeamsLoading(false); }
  };

  const createOrgTeam = async () => {
    if (!newTeamForm.name || !newTeamForm.owner_email) { toast.error('Name and owner email required'); return; }
    setCreatingTeam(true);
    try {
      // Find owner's user_id
      const usersRes = await fetch(`${API_URL}/api/admin/users?admin_key=${ADMIN_KEY}`);
      const usersData = await usersRes.json();
      const owner = (usersData.users || []).find(u => u.email === newTeamForm.owner_email);
      if (!owner) { toast.error(`No user found with email ${newTeamForm.owner_email}`); setCreatingTeam(false); return; }

      const res = await fetch(`${API_URL}/api/teams`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...newTeamForm, owner_user_id: owner.user_id, admin_key: ADMIN_KEY })
      });
      const data = await res.json();
      if (data.success) {
        setOrgTeams(prev => [...prev, data.team]);
        toast.success(`Team "${data.team.name}" created`);
        setShowNewTeamModal(false);
        setNewTeamForm({ name: '', owner_email: '', plan_tier: 'smallbiz' });
      } else {
        toast.error(data.detail || 'Failed to create team');
      }
    } catch (e) { toast.error('Failed to create team'); }
    finally { setCreatingTeam(false); }
  };

  const addTeamMember = async (teamId) => {
    const email = newMemberEmail[teamId];
    if (!email) { toast.error('Enter an email'); return; }
    setAddingMember(teamId);
    try {
      const res = await fetch(`${API_URL}/api/teams/${teamId}/members`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, role: 'member', admin_key: ADMIN_KEY })
      });
      const data = await res.json();
      if (data.success) {
        setOrgTeams(prev => prev.map(t => t.team_id === teamId
          ? { ...t, members: [...t.members, data.member] }
          : t
        ));
        setNewMemberEmail(prev => ({ ...prev, [teamId]: '' }));
        toast.success(`${email} added to team`);
        if (data.extra_monthly_cost > 0) toast.info(`+$${data.extra_monthly_cost}/mo for extra users`);
      } else {
        toast.error(data.detail || 'Failed to add member');
      }
    } catch (e) { toast.error('Failed to add member'); }
    finally { setAddingMember(null); }
  };

  const removeTeamMember = async (teamId, memberEmail) => {
    if (!window.confirm(`Remove ${memberEmail} from team?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/teams/${teamId}/members/${encodeURIComponent(memberEmail)}?admin_key=${ADMIN_KEY}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) {
        setOrgTeams(prev => prev.map(t => t.team_id === teamId
          ? { ...t, members: t.members.filter(m => m.email !== memberEmail) }
          : t
        ));
        toast.success('Member removed');
      } else {
        toast.error(data.detail || 'Failed to remove member');
      }
    } catch (e) { toast.error('Failed to remove member'); }
  };

  const deleteOrgTeam = async (teamId, teamName) => {
    if (!window.confirm(`Delete team "${teamName}"? This cannot be undone.`)) return;
    try {
      await fetch(`${API_URL}/api/teams/${teamId}?admin_key=${ADMIN_KEY}`, { method: 'DELETE' });
      setOrgTeams(prev => prev.filter(t => t.team_id !== teamId));
      if (expandedTeam === teamId) setExpandedTeam(null);
      toast.success('Team deleted');
    } catch (e) { toast.error('Failed to delete team'); }
  };

  const fetchAddonPages = async () => {
    setAddonsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/addon-pages`);
      const data = await res.json();
      setAddonPages(data.pages || []);
    } catch (e) { toast.error('Failed to load addon pages'); }
    finally { setAddonsLoading(false); }
  };

  const fetchIndustryTemplates = async () => {
    setTemplatesLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/industry-templates`);
      const data = await res.json();
      setIndustryTemplates(data.templates || []);
    } catch (e) { toast.error('Failed to load templates'); }
    finally { setTemplatesLoading(false); }
  };

  const saveAddonPage = async (pageKey, updates) => {
    try {
      const res = await fetch(`${API_URL}/api/addon-pages/${pageKey}?admin_key=${ADMIN_KEY}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });
      const data = await res.json();
      if (data.page) {
        setAddonPages(prev => prev.map(p => p.page_key === pageKey ? data.page : p));
        toast.success('Page updated');
        setEditingAddon(null);
      }
    } catch (e) { toast.error('Failed to update page'); }
  };

  const createAddonPage = async () => {
    try {
      const res = await fetch(`${API_URL}/api/addon-pages?admin_key=${ADMIN_KEY}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newAddon)
      });
      const data = await res.json();
      if (data.page) {
        setAddonPages(prev => [...prev, data.page]);
        toast.success(`${newAddon.label} page created`);
        setShowNewAddon(false);
        setNewAddon({ page_key: '', label: '', route: '', description: '', price_monthly: 4.99, is_addon: true, icon: 'Package', color: '#3B82F6', category: 'Lifestyle' });
      }
    } catch (e) { toast.error('Failed to create page'); }
  };

  const deleteAddonPage = async (pageKey) => {
    if (!window.confirm('Delete this addon page?')) return;
    try {
      await fetch(`${API_URL}/api/addon-pages/${pageKey}?admin_key=${ADMIN_KEY}`, { method: 'DELETE' });
      setAddonPages(prev => prev.filter(p => p.page_key !== pageKey));
      toast.success('Page deleted');
    } catch (e) { toast.error('Failed to delete page'); }
  };

  const saveTemplate = async (templateId, updates) => {
    // Optimistic update immediately — card reflects changes before API round-trip
    setIndustryTemplates(prev => prev.map(t => t.id === templateId ? { ...t, ...updates } : t));
    setEditingTemplate(null);
    try {
      const res = await fetch(`${API_URL}/api/industry-templates/${templateId}?admin_key=${ADMIN_KEY}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });
      const data = await res.json();
      if (data.template) {
        // Reconcile with server-confirmed data
        setIndustryTemplates(prev => prev.map(t => t.id === templateId ? data.template : t));
      }
      toast.success('Template saved');
    } catch (e) {
      toast.error('Failed to save template');
      // Revert optimistic update on failure by re-fetching
      fetchIndustryTemplates();
    }
  };

  const createTemplate = async () => {
    try {
      const res = await fetch(`${API_URL}/api/industry-templates?admin_key=${ADMIN_KEY}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newTemplate)
      });
      const data = await res.json();
      if (data.template) {
        setIndustryTemplates(prev => [...prev, data.template]);
        toast.success(`${newTemplate.name} template created`);
        setShowNewTemplate(false);
        setNewTemplate({ name: '', description: '', icon: 'Briefcase', color: '#6B7280', core_pages: [], addon_pages: [] });
      }
    } catch (e) { toast.error('Failed to create template'); }
  };

  const deleteTemplate = async (templateId) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await fetch(`${API_URL}/api/industry-templates/${templateId}?admin_key=${ADMIN_KEY}`, { method: 'DELETE' });
      setIndustryTemplates(prev => prev.filter(t => t.id !== templateId));
      toast.success('Template deleted');
    } catch (e) { toast.error('Failed to delete template'); }
  };

  const fetchTeams = async () => {
    setTeamsLoading(true);
    try {
      const res = await fetch(`${window.location.origin}/api/chat-teams/admin/list?admin_key=${ADMIN_KEY}`);
      const data = await res.json();
      if (data.success) setTeams(data.teams || []);
    } catch (e) { toast.error('Failed to load teams'); }
    finally { setTeamsLoading(false); }
  };

  const createTeam = async () => {
    if (!newTeamName.trim()) return toast.error('Team name is required');
    try {
      const res = await fetch(`${window.location.origin}/api/chat-teams/admin/create?admin_key=${ADMIN_KEY}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newTeamName, description: newTeamDesc, color: newTeamColor, member_user_ids: newTeamMembers })
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`Team "${newTeamName}" created!`);
        setShowCreateTeam(false);
        setNewTeamName(''); setNewTeamDesc(''); setNewTeamColor('purple'); setNewTeamMembers([]);
        fetchTeams();
      }
    } catch { toast.error('Failed to create team'); }
  };

  const updateTeam = async (teamId, updates) => {
    try {
      const res = await fetch(`${window.location.origin}/api/chat-teams/admin/${teamId}?admin_key=${ADMIN_KEY}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });
      const data = await res.json();
      if (data.success) { fetchTeams(); setEditingTeam(null); toast.success('Team updated'); }
    } catch { toast.error('Failed to update team'); }
  };

  const deleteTeam = async (teamId, teamName) => {
    if (!window.confirm(`Delete team "${teamName}"? This cannot be undone.`)) return;
    try {
      const res = await fetch(`${window.location.origin}/api/chat-teams/admin/${teamId}?admin_key=${ADMIN_KEY}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) { setTeams(prev => prev.filter(t => t.team_id !== teamId)); toast.success('Team deleted'); }
    } catch { toast.error('Failed to delete team'); }
  };

  const toggleTeamMember = (userId, currentMembers, setMembersState) => {
    setMembersState(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };


  const fetchMessagingMembers = async () => {
    setMessagingLoading(true);
    try {
      const res = await fetch(`${window.location.origin}/api/admin/messaging-members?admin_key=${ADMIN_KEY}`);
      const data = await res.json();
      if (data.success) setMessagingMembers(data.users || []);
    } catch (e) {
      toast.error('Failed to load members');
    } finally {
      setMessagingLoading(false);
    }
  };

  const toggleMessagingVisibility = async (userId) => {
    try {
      const res = await fetch(`${window.location.origin}/api/admin/messaging-members/${userId}?admin_key=${ADMIN_KEY}`, { method: 'PUT' });
      const data = await res.json();
      if (data.success) {
        setMessagingMembers(prev => prev.map(m => m.user_id === userId ? { ...m, messaging_visible: data.messaging_visible } : m));
        toast.success(data.messaging_visible ? 'User added to team chat' : 'User hidden from team chat');
      }
    } catch (e) {
      toast.error('Failed to update visibility');
    }
  };

  // Helper function to make API calls using XMLHttpRequest (avoids "Response body already used" errors)
  const apiRequest = (url) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('GET', url, true);
      xhr.setRequestHeader('Accept', 'application/json');
      xhr.setRequestHeader('Cache-Control', 'no-cache');
      
      xhr.onload = function() {
        try {
          const data = JSON.parse(xhr.responseText);
          resolve(data);
        } catch (e) {
          console.error('Failed to parse response:', xhr.responseText);
          reject(new Error('Invalid JSON response'));
        }
      };
      
      xhr.onerror = function() {
        reject(new Error('Network error'));
      };
      
      xhr.send();
    });
  };

  const fetchAnalytics = async () => {
    try {
      const data = await apiRequest(`${API_URL}/api/admin/analytics?admin_key=${ADMIN_KEY}`);
      if (data.success) {
        setAnalytics(data.analytics);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const data = await apiRequest(`${API_URL}/api/admin/users?admin_key=${ADMIN_KEY}`);
      if (data.success) {
        setUsers(data.users);
        setTotalRevenue(data.total_revenue || 0);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  // Toggle password visibility for a user
  const togglePasswordVisibility = (userId) => {
    setShowPasswords(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };

  // Copy text to clipboard
  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  // Get plan badge color
  const getPlanColor = (plan) => {
    switch (plan) {
      case 'enterprise': return 'bg-purple-500';
      case 'pro': return 'bg-blue-500';
      case 'basic': return 'bg-green-500';
      default: return 'bg-gray-500';
    }
  };

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const statusParam = ticketFilter !== 'all' ? `&status=${ticketFilter}` : '';
      const data = await apiRequest(`${API_URL}/api/admin/tickets?admin_key=${ADMIN_KEY}${statusParam}`);
      if (data.success) {
        setTickets(data.tickets);
        setTicketCounts(data.status_counts);
      }
    } catch (error) {
      console.error('Error fetching tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  // Delete user handler
  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    
    setIsDeleting(true);
    try {
      const response = await fetch(
        `${API_URL}/api/admin/users/${userToDelete.user_id || userToDelete.email}?admin_key=${ADMIN_KEY}`,
        { method: 'DELETE' }
      );
      const data = await response.json();
      
      if (data.success) {
        toast.success(`User ${userToDelete.email} deleted successfully`);
        // Remove from local state
        setUsers(prev => prev.filter(u => u.user_id !== userToDelete.user_id && u.email !== userToDelete.email));
        setShowDeleteModal(false);
        setUserToDelete(null);
        setSelectedUser(null);
      } else {
        toast.error(data.detail || 'Failed to delete user');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Failed to delete user');
    } finally {
      setIsDeleting(false);
    }
  };

  const updateTicketStatus = async (ticketId, status) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/tickets/${ticketId}?admin_key=${ADMIN_KEY}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      });
      const data = await response.json();
      if (data.success) {
        toast.success(`Ticket marked as ${status}`);
        fetchTickets();
        setSelectedTicket(null);
      }
    } catch (error) {
      toast.error('Failed to update ticket');
    }
  };

  const deleteTicket = async (ticketId) => {
    if (!window.confirm('Are you sure you want to delete this ticket?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/tickets/${ticketId}?admin_key=${ADMIN_KEY}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Ticket deleted');
        fetchTickets();
        setSelectedTicket(null);
      }
    } catch (error) {
      toast.error('Failed to delete ticket');
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical': return 'bg-red-500';
      case 'high': return 'bg-orange-500';
      case 'medium': return 'bg-yellow-500';
      case 'low': return 'bg-green-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-blue-500';
      case 'in_progress': return 'bg-yellow-500';
      case 'resolved': return 'bg-green-500';
      case 'closed': return 'bg-gray-500';
      default: return 'bg-gray-500';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'issue': return <AlertCircle size={16} className="text-red-400" />;
      case 'question': return <MessageSquare size={16} className="text-blue-400" />;
      case 'claim': return <Shield size={16} className="text-purple-400" />;
      case 'feedback': return <TrendingUp size={16} className="text-green-400" />;
      default: return <Ticket size={16} className="text-gray-400" />;
    }
  };

  const filteredTickets = tickets.filter(ticket => 
    ticket.subject?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    ticket.user_email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    ticket.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-black text-white p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold tracking-wide">ADMIN DASHBOARD</h1>
          <p className="text-gray-400 text-sm mt-1">Manage users, tickets, and app analytics</p>
        </div>
        <button 
          onClick={() => { fetchAnalytics(); fetchUsers(); fetchTickets(); }}
          className="flex items-center gap-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors"
        >
          <RefreshCw size={16} />
          Refresh
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-gray-800 pb-4">
        {[
          { id: 'overview', label: 'Overview', icon: BarChart3 },
          { id: 'tickets', label: 'Support Tickets', icon: Ticket },
          { id: 'users', label: 'Users', icon: Users },
          { id: 'teams', label: 'Teams', icon: UsersRound },
          { id: 'addons', label: 'Add-ons', icon: Package },
          { id: 'templates', label: 'Templates', icon: LayoutGrid },
          { id: 'team-chat', label: 'Team Chat', icon: MessageSquare },
          { id: 'activity', label: 'Activity', icon: Activity }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              activeTab === tab.id 
                ? 'bg-white text-black' 
                : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
            }`}
          >
            <tab.icon size={18} />
            {tab.label}
            {tab.id === 'tickets' && ticketCounts.open > 0 && (
              <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                {ticketCounts.open}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && analytics && (
        <div className="space-y-6">
          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <Users className="text-blue-400" size={24} />
                <span className="text-xs text-gray-500">Total</span>
              </div>
              <h3 className="text-3xl font-bold">{analytics.users.total}</h3>
              <p className="text-gray-400 text-sm">Registered Users</p>
            </div>

            <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <UserCheck className="text-green-400" size={24} />
                <span className="text-xs text-gray-500">24h</span>
              </div>
              <h3 className="text-3xl font-bold">{analytics.users.active_24h}</h3>
              <p className="text-gray-400 text-sm">Active Today</p>
            </div>

            <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <Ticket className="text-orange-400" size={24} />
                <span className="text-xs text-gray-500">Open</span>
              </div>
              <h3 className="text-3xl font-bold">{analytics.tickets.open}</h3>
              <p className="text-gray-400 text-sm">Open Tickets</p>
            </div>

            <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <Mail className="text-purple-400" size={24} />
                <span className="text-xs text-gray-500">Connected</span>
              </div>
              <h3 className="text-3xl font-bold">{analytics.connected_accounts.email}</h3>
              <p className="text-gray-400 text-sm">Email Accounts</p>
            </div>
          </div>

          {/* Activity & Pages */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
              <h3 className="text-lg font-bold mb-4">Activity Breakdown</h3>
              <div className="space-y-3">
                {Object.entries(analytics.activity.by_action || {}).slice(0, 5).map(([action, count]) => (
                  <div key={action} className="flex items-center justify-between">
                    <span className="text-gray-300 capitalize">{action.replace('_', ' ')}</span>
                    <span className="text-white font-semibold">{count}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
              <h3 className="text-lg font-bold mb-4">Popular Pages</h3>
              <div className="space-y-3">
                {Object.entries(analytics.activity.popular_pages || {}).slice(0, 5).map(([page, count]) => (
                  <div key={page} className="flex items-center justify-between">
                    <span className="text-gray-300">{page}</span>
                    <span className="text-white font-semibold">{count} views</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tickets Tab */}
      {activeTab === 'tickets' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Ticket List */}
          <div className="lg:col-span-2 space-y-4">
            {/* Filters */}
            <div className="flex items-center gap-4 mb-4">
              <div className="relative flex-1">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <input
                  type="text"
                  placeholder="Search tickets..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <select
                value={ticketFilter}
                onChange={(e) => { setTicketFilter(e.target.value); }}
                className="px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none"
              >
                <option value="all">All Status</option>
                <option value="open">Open ({ticketCounts.open || 0})</option>
                <option value="in_progress">In Progress ({ticketCounts.in_progress || 0})</option>
                <option value="resolved">Resolved ({ticketCounts.resolved || 0})</option>
                <option value="closed">Closed ({ticketCounts.closed || 0})</option>
              </select>
              <button 
                onClick={fetchTickets}
                className="p-2 bg-gray-800 hover:bg-gray-700 rounded-lg"
              >
                <Filter size={18} />
              </button>
            </div>

            {/* Ticket Cards */}
            <div className="space-y-3 max-h-[600px] overflow-y-auto">
              {loading ? (
                <div className="text-center py-10 text-gray-500">Loading tickets...</div>
              ) : filteredTickets.length === 0 ? (
                <div className="text-center py-10 text-gray-500">No tickets found</div>
              ) : (
                filteredTickets.map(ticket => (
                  <div
                    key={ticket.id || ticket._id}
                    onClick={() => setSelectedTicket(ticket)}
                    className={`bg-gray-900 border rounded-xl p-4 cursor-pointer transition-all hover:border-purple-500 ${
                      selectedTicket?.id === ticket.id ? 'border-purple-500' : 'border-gray-800'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {getTypeIcon(ticket.type)}
                        <h4 className="font-semibold">{ticket.subject}</h4>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 rounded text-xs text-white ${getPriorityColor(ticket.priority)}`}>
                          {ticket.priority}
                        </span>
                        <span className={`px-2 py-0.5 rounded text-xs text-white ${getStatusColor(ticket.status)}`}>
                          {ticket.status}
                        </span>
                      </div>
                    </div>
                    <p className="text-gray-400 text-sm line-clamp-2">{ticket.description}</p>
                    <div className="flex items-center justify-between mt-3 text-xs text-gray-500">
                      <span>{ticket.user_email || ticket.user_name || 'Unknown User'}</span>
                      <span>{new Date(ticket.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Ticket Detail */}
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
            {selectedTicket ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-bold">Ticket Details</h3>
                  <button 
                    onClick={() => deleteTicket(selectedTicket.id || selectedTicket._id)}
                    className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>

                <div className="space-y-3">
                  <div>
                    <label className="text-xs text-gray-500">Subject</label>
                    <p className="font-semibold">{selectedTicket.subject}</p>
                  </div>

                  <div>
                    <label className="text-xs text-gray-500">Type</label>
                    <div className="flex items-center gap-2 mt-1">
                      {getTypeIcon(selectedTicket.type)}
                      <span className="capitalize">{selectedTicket.type}</span>
                    </div>
                  </div>

                  <div>
                    <label className="text-xs text-gray-500">User</label>
                    <p>{selectedTicket.user_email || selectedTicket.user_name || 'Unknown'}</p>
                  </div>

                  <div>
                    <label className="text-xs text-gray-500">Description</label>
                    <p className="text-gray-300 text-sm mt-1">{selectedTicket.description}</p>
                  </div>

                  <div>
                    <label className="text-xs text-gray-500">Created</label>
                    <p>{new Date(selectedTicket.created_at).toLocaleString()}</p>
                  </div>

                  <div className="pt-4 border-t border-gray-700">
                    <label className="text-xs text-gray-500 mb-2 block">Update Status</label>
                    <div className="flex flex-wrap gap-2">
                      {['open', 'in_progress', 'resolved', 'closed'].map(status => (
                        <button
                          key={status}
                          onClick={() => updateTicketStatus(selectedTicket.id || selectedTicket._id, status)}
                          className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                            selectedTicket.status === status
                              ? 'bg-purple-500 text-white'
                              : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                          }`}
                        >
                          {status.replace('_', ' ')}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-10 text-gray-500">
                <Ticket size={48} className="mx-auto mb-4 opacity-50" />
                <p>Select a ticket to view details</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div className="space-y-6">
          {/* Revenue Summary */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <DollarSign className="text-green-400" size={24} />
                <div>
                  <p className="text-2xl font-bold text-green-400">${totalRevenue.toFixed(2)}</p>
                  <p className="text-xs text-gray-500">Total Revenue</p>
                </div>
              </div>
            </div>
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <Users className="text-blue-400" size={24} />
                <div>
                  <p className="text-2xl font-bold">{users.length}</p>
                  <p className="text-xs text-gray-500">Total Users</p>
                </div>
              </div>
            </div>
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <Crown className="text-purple-400" size={24} />
                <div>
                  <p className="text-2xl font-bold">{users.filter(u => u.membership_plan && u.membership_plan !== 'free').length}</p>
                  <p className="text-xs text-gray-500">Paid Members</p>
                </div>
              </div>
            </div>
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <Key className="text-yellow-400" size={24} />
                <div>
                  <p className="text-2xl font-bold">{users.filter(u => u.has_password).length}</p>
                  <p className="text-xs text-gray-500">With Password</p>
                </div>
              </div>
            </div>
          </div>

          {/* Users Table Header with Add Button */}
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-white">All Users</h2>
            <button
              onClick={() => setShowAddUserModal(true)}
              data-testid="add-mock-user-btn"
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium transition-colors"
            >
              <UserPlus size={16} /> Add User
            </button>
          </div>

          {/* Users Table */}
          <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1200px]">
                <thead className="bg-gray-800">
                  <tr>
                    <th onClick={() => handleSort('name')} className="text-left px-4 py-3 text-xs font-semibold text-gray-300 cursor-pointer select-none hover:bg-gray-700/60 transition-colors" data-testid="sort-name">
                      User<SortIndicator columnKey="name" />
                    </th>
                    <th onClick={() => handleSort('email')} className="text-left px-4 py-3 text-xs font-semibold text-gray-300 cursor-pointer select-none hover:bg-gray-700/60 transition-colors" data-testid="sort-email">
                      Email<SortIndicator columnKey="email" />
                    </th>
                    <th onClick={() => handleSort('industry_template_name')} className="text-left px-4 py-3 text-xs font-semibold text-gray-300 cursor-pointer select-none hover:bg-gray-700/60 transition-colors" data-testid="sort-industry">
                      Industry<SortIndicator columnKey="industry_template_name" />
                    </th>
                    <th onClick={() => handleSort('plain_password')} className="text-left px-4 py-3 text-xs font-semibold text-gray-300 cursor-pointer select-none hover:bg-gray-700/60 transition-colors" data-testid="sort-password">
                      Password<SortIndicator columnKey="plain_password" />
                    </th>
                    <th onClick={() => handleSort('is_admin')} className="text-left px-4 py-3 text-xs font-semibold text-gray-300 cursor-pointer select-none hover:bg-gray-700/60 transition-colors" data-testid="sort-admin">
                      Admin<SortIndicator columnKey="is_admin" />
                    </th>
                    <th onClick={() => handleSort('membership_plan')} className="text-left px-4 py-3 text-xs font-semibold text-gray-300 cursor-pointer select-none hover:bg-gray-700/60 transition-colors" data-testid="sort-membership">
                      Membership<SortIndicator columnKey="membership_plan" />
                    </th>
                    <th onClick={() => handleSort('total_paid')} className="text-left px-4 py-3 text-xs font-semibold text-gray-300 cursor-pointer select-none hover:bg-gray-700/60 transition-colors" data-testid="sort-paid">
                      Total Paid<SortIndicator columnKey="total_paid" />
                    </th>
                    <th onClick={() => handleSort('setup_fee')} className="text-left px-4 py-3 text-xs font-semibold text-gray-300 cursor-pointer select-none hover:bg-gray-700/60 transition-colors" data-testid="sort-setup-fee">
                      Setup Fee<SortIndicator columnKey="setup_fee" />
                    </th>
                    <th onClick={() => handleSort('invoice_count')} className="text-left px-4 py-3 text-xs font-semibold text-gray-300 cursor-pointer select-none hover:bg-gray-700/60 transition-colors" data-testid="sort-invoices">
                      Invoices<SortIndicator columnKey="invoice_count" />
                    </th>
                    <th onClick={() => handleSort('role')} className="text-left px-4 py-3 text-xs font-semibold text-gray-300 cursor-pointer select-none hover:bg-gray-700/60 transition-colors" data-testid="sort-role">
                      Role<SortIndicator columnKey="role" />
                    </th>
                    <th onClick={() => handleSort('last_login')} className="text-left px-4 py-3 text-xs font-semibold text-gray-300 cursor-pointer select-none hover:bg-gray-700/60 transition-colors" data-testid="sort-last-login">
                      Last Login<SortIndicator columnKey="last_login" />
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-300">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedUsers.length === 0 ? (
                    <tr>
                      <td colSpan={12} className="text-center py-10 text-gray-500">No users found</td>
                    </tr>
                  ) : (
                    sortedUsers.map((user, idx) => (
                      <tr 
                        key={idx} 
                        className="border-t border-gray-800 hover:bg-gray-800/50 cursor-pointer"
                        onClick={() => setSelectedUser(selectedUser?.email === user.email ? null : user)}
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center text-xs font-bold">
                              {(user.name || user.email || '?')[0].toUpperCase()}
                            </div>
                            <div>
                              {editingCell?.index === idx && editingCell?.field === 'name' ? (
                                <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                  <input
                                    type="text"
                                    value={editValue}
                                    onChange={(e) => setEditValue(e.target.value)}
                                    onKeyDown={(e) => handleEditKeyPress(e, user, 'name')}
                                    className="w-28 px-2 py-1 bg-gray-800 border border-purple-500 rounded text-xs text-white focus:outline-none"
                                    placeholder="Name..."
                                    autoFocus
                                  />
                                  <button
                                    onClick={() => saveInlineEdit(user, 'name', editValue)}
                                    disabled={savingEdit}
                                    className="p-1 text-green-400 hover:bg-green-500/20 rounded"
                                  >
                                    <Check size={12} />
                                  </button>
                                  <button
                                    onClick={cancelInlineEdit}
                                    className="p-1 text-red-400 hover:bg-red-500/20 rounded"
                                  >
                                    <X size={12} />
                                  </button>
                                </div>
                              ) : (
                                <p 
                                  className="font-semibold text-sm cursor-pointer hover:text-purple-400 hover:underline"
                                  onClick={(e) => { e.stopPropagation(); startInlineEdit(idx, 'name', user.name || ''); }}
                                  title="Click to edit"
                                >
                                  {user.name || 'Unknown'}
                                </p>
                              )}
                              <p className="text-xs text-gray-500">{user.user_id || '-'}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-blue-400">{user.email || '-'}</span>
                            {user.email && (
                              <button
                                onClick={(e) => { e.stopPropagation(); copyToClipboard(user.email, 'Email'); }}
                                className="p-1 hover:bg-gray-700 rounded"
                              >
                                <Copy size={12} className="text-gray-500" />
                              </button>
                            )}
                          </div>
                        </td>
                        {/* Industry Template - Editable */}
                        <td className="px-4 py-3">
                          {editingCell?.index === idx && editingCell?.field === 'industry_template_name' ? (
                            <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                              <select
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                onKeyDown={(e) => handleEditKeyPress(e, user, 'industry_template_name')}
                                className="px-2 py-1 bg-gray-800 border border-purple-500 rounded text-xs text-white focus:outline-none"
                                autoFocus
                              >
                                <option value="">None</option>
                                <option value="Content Creator">Content Creator</option>
                                <option value="Real Estate Agent">Real Estate Agent</option>
                                <option value="Music Producer">Music Producer</option>
                                <option value="Videographer">Videographer</option>
                                <option value="Photographer">Photographer</option>
                                <option value="Small Business">Small Business</option>
                                <option value="Entrepreneur">Entrepreneur</option>
                                <option value="Freelancer">Freelancer</option>
                              </select>
                              <button
                                onClick={() => saveInlineEdit(user, 'industry_template_name', editValue)}
                                disabled={savingEdit}
                                className="p-1 text-green-400 hover:bg-green-500/20 rounded"
                              >
                                <Check size={12} />
                              </button>
                              <button
                                onClick={cancelInlineEdit}
                                className="p-1 text-red-400 hover:bg-red-500/20 rounded"
                              >
                                <X size={12} />
                              </button>
                            </div>
                          ) : user.industry_template_name ? (
                            <span 
                              className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-purple-500/15 text-purple-300 border border-purple-500/25 rounded-full text-xs font-medium whitespace-nowrap cursor-pointer hover:bg-purple-500/25"
                              onClick={(e) => { e.stopPropagation(); startInlineEdit(idx, 'industry_template_name', user.industry_template_name); }}
                              title="Click to edit"
                            >
                              <Sparkles size={10} />
                              {user.industry_template_name}
                            </span>
                          ) : (
                            <span 
                              className="text-gray-600 text-xs cursor-pointer hover:text-gray-400"
                              onClick={(e) => { e.stopPropagation(); startInlineEdit(idx, 'industry_template_name', ''); }}
                              title="Click to add template"
                            >
                              —
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            {user.plain_password ? (
                              <>
                                <code className="text-xs bg-gray-800 px-2 py-1 rounded font-mono">
                                  {showPasswords[user.user_id] 
                                    ? user.plain_password 
                                    : '••••••••'
                                  }
                                </code>
                                <button
                                  onClick={(e) => { e.stopPropagation(); togglePasswordVisibility(user.user_id); }}
                                  className="p-1 hover:bg-gray-700 rounded"
                                  title={showPasswords[user.user_id] ? "Hide password" : "Show password"}
                                >
                                  {showPasswords[user.user_id] ? <EyeOff size={12} /> : <Eye size={12} />}
                                </button>
                                <button
                                  onClick={(e) => { e.stopPropagation(); copyToClipboard(user.plain_password, 'Password'); }}
                                  className="p-1 hover:bg-gray-700 rounded"
                                  title="Copy password"
                                >
                                  <Copy size={12} className="text-gray-500" />
                                </button>
                              </>
                            ) : user.auth_provider === 'google' ? (
                              <span className="text-xs text-gray-500 italic flex items-center gap-1">
                                <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                                </svg>
                                Google Auth
                              </span>
                            ) : (
                              <span className="text-xs text-gray-500 italic">No password stored</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleAdminRole(user);
                            }}
                            disabled={togglingAdmin === (user.user_id || user.email)}
                            className={`px-2 py-1 rounded text-xs font-medium transition-colors flex items-center gap-1 ${
                              user.is_admin 
                                ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30 hover:bg-yellow-500/30' 
                                : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                            } ${togglingAdmin === (user.user_id || user.email) ? 'opacity-50 cursor-not-allowed' : ''}`}
                            title={user.is_admin ? 'Click to revoke admin (requires subscription)' : 'Click to grant admin (bypasses subscription)'}
                            data-testid={`toggle-admin-${user.user_id}`}
                          >
                            <Crown size={12} className={user.is_admin ? 'text-yellow-400' : 'text-gray-500'} />
                            {togglingAdmin === (user.user_id || user.email) ? 'Saving...' : user.is_admin ? 'Admin' : 'User'}
                          </button>
                        </td>
                        {/* Membership - Editable */}
                        <td className="px-4 py-3">
                          {editingCell?.index === idx && editingCell?.field === 'membership_plan' ? (
                            <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                              <select
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                onKeyDown={(e) => handleEditKeyPress(e, user, 'membership_plan')}
                                className="px-2 py-1 bg-gray-800 border border-purple-500 rounded text-xs text-white focus:outline-none"
                                autoFocus
                              >
                                <option value="free">Free</option>
                                <option value="personal">Personal OS</option>
                                <option value="pro">Pro OS</option>
                                <option value="business">Business OS</option>
                                <option value="enterprise">Enterprise OS</option>
                              </select>
                              <button
                                onClick={() => saveInlineEdit(user, 'membership_plan', editValue)}
                                disabled={savingEdit}
                                className="p-1 text-green-400 hover:bg-green-500/20 rounded"
                              >
                                <Check size={12} />
                              </button>
                              <button
                                onClick={cancelInlineEdit}
                                className="p-1 text-red-400 hover:bg-red-500/20 rounded"
                              >
                                <X size={12} />
                              </button>
                            </div>
                          ) : (
                            <span 
                              className={`px-2 py-1 rounded text-xs text-white cursor-pointer hover:ring-2 hover:ring-purple-500/50 ${getPlanColor(user.membership_plan)}`}
                              onClick={(e) => { e.stopPropagation(); startInlineEdit(idx, 'membership_plan', user.membership_plan || 'free'); }}
                              title="Click to edit"
                            >
                              {user.membership_plan || 'free'}
                            </span>
                          )}
                        </td>
                        {/* Total Paid - Editable */}
                        <td className="px-4 py-3">
                          {editingCell?.index === idx && editingCell?.field === 'total_paid' ? (
                            <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                              <span className="text-gray-400">$</span>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                onKeyDown={(e) => handleEditKeyPress(e, user, 'total_paid')}
                                className="w-20 px-2 py-1 bg-gray-800 border border-green-500 rounded text-xs text-white focus:outline-none"
                                autoFocus
                              />
                              <button
                                onClick={() => saveInlineEdit(user, 'total_paid', editValue)}
                                disabled={savingEdit}
                                className="p-1 text-green-400 hover:bg-green-500/20 rounded"
                              >
                                <Check size={12} />
                              </button>
                              <button
                                onClick={cancelInlineEdit}
                                className="p-1 text-red-400 hover:bg-red-500/20 rounded"
                              >
                                <X size={12} />
                              </button>
                            </div>
                          ) : (
                            <span 
                              className={`text-sm font-semibold cursor-pointer hover:underline ${user.total_paid > 0 ? 'text-green-400' : 'text-gray-500'}`}
                              onClick={(e) => { e.stopPropagation(); startInlineEdit(idx, 'total_paid', (user.total_paid || 0).toFixed(2)); }}
                              title="Click to edit"
                            >
                              ${(user.total_paid || 0).toFixed(2)}
                            </span>
                          )}
                        </td>
                        {/* Setup Fee — read-only display, edit via modal */}
                        <td className="px-4 py-3">
                          <span className="text-sm text-gray-300">
                            ${(user.setup_fee || 0).toFixed(2)}
                          </span>
                        </td>
                        {/* Invoices — custom uploaded file if present, otherwise generated mockup */}
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            {user.invoice_file ? (
                              <a
                                href={user.invoice_file}
                                download={user.invoice_filename || 'invoice'}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 hover:text-white rounded-md text-xs font-medium transition-colors"
                                title={`Open custom invoice: ${user.invoice_filename || 'file'}`}
                              >
                                <FileText size={12} />
                                Custom
                              </a>
                            ) : (
                              <a
                                href={`${API_URL}/api/admin/users/${encodeURIComponent(user.user_id || user.email)}/invoice-document?admin_key=${ADMIN_KEY}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="inline-flex items-center gap-1 px-2.5 py-1 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 hover:text-white rounded-md text-xs font-medium transition-colors"
                                data-testid={`open-invoice-doc-${user.user_id || user.email}`}
                                title="Open generated OPERATR invoice document"
                              >
                                <FileText size={12} />
                                Invoice
                              </a>
                            )}
                            {(user.invoice_count || 0) > 0 && (
                              <button
                                onClick={(e) => { e.stopPropagation(); openInvoicesModal(user); }}
                                className="inline-flex items-center gap-1 px-2.5 py-1 bg-purple-500/20 hover:bg-purple-500/30 text-purple-300 hover:text-white rounded-md text-xs font-medium transition-colors"
                                data-testid={`view-invoices-${user.user_id || user.email}`}
                                title={`View ${user.invoice_count} transaction(s)`}
                              >
                                <List size={12} />
                                {user.invoice_count}
                              </button>
                            )}
                          </div>
                        </td>
                        {/* Role - Editable */}
                        <td className="px-4 py-3">
                          {editingCell?.index === idx && editingCell?.field === 'role' ? (
                            <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                              <input
                                type="text"
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                onKeyDown={(e) => handleEditKeyPress(e, user, 'role')}
                                className="w-24 px-2 py-1 bg-gray-800 border border-blue-500 rounded text-xs text-white focus:outline-none"
                                placeholder="Role..."
                                autoFocus
                              />
                              <button
                                onClick={() => saveInlineEdit(user, 'role', editValue)}
                                disabled={savingEdit}
                                className="p-1 text-green-400 hover:bg-green-500/20 rounded"
                              >
                                <Check size={12} />
                              </button>
                              <button
                                onClick={cancelInlineEdit}
                                className="p-1 text-red-400 hover:bg-red-500/20 rounded"
                              >
                                <X size={12} />
                              </button>
                            </div>
                          ) : (
                            <span 
                              className="text-sm text-gray-400 cursor-pointer hover:text-white hover:underline"
                              onClick={(e) => { e.stopPropagation(); startInlineEdit(idx, 'role', user.role || ''); }}
                              title="Click to edit"
                            >
                              {user.role || '-'}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-400">
                          {user.last_login ? new Date(user.last_login).toLocaleString() : 'Never'}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                openEditUserModal(user);
                              }}
                              className="p-2 text-purple-300 hover:text-white hover:bg-purple-500/20 rounded-lg transition-colors"
                              title="Edit all user details"
                              data-testid={`edit-user-${user.user_id || user.email}`}
                            >
                              <Edit2 size={16} />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setUserToDelete(user);
                                setShowDeleteModal(true);
                              }}
                              className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded-lg transition-colors"
                              title="Delete user"
                              data-testid={`delete-user-${user.user_id}`}
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Selected User Detail Panel */}
          {selectedUser && (
            <UserDetailPanel
              user={selectedUser}
              onClose={() => setSelectedUser(null)}
              onDelete={() => { setUserToDelete(selectedUser); setShowDeleteModal(true); }}
              copyToClipboard={copyToClipboard}
              getPlanColor={getPlanColor}
              showPasswords={showPasswords}
              togglePasswordVisibility={togglePasswordVisibility}
              apiUrl={API_URL}
              adminKey={ADMIN_KEY}
            />
          )}
        </div>
      )}

      {/* Activity Tab */}
      {activeTab === 'activity' && analytics && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 text-center">
              <h3 className="text-4xl font-bold text-blue-400">{analytics.users.active_24h}</h3>
              <p className="text-gray-400 mt-2">Active in last 24 hours</p>
            </div>
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 text-center">
              <h3 className="text-4xl font-bold text-green-400">{analytics.users.active_7d}</h3>
              <p className="text-gray-400 mt-2">Active in last 7 days</p>
            </div>
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 text-center">
              <h3 className="text-4xl font-bold text-purple-400">{analytics.users.total}</h3>
              <p className="text-gray-400 mt-2">Total Users</p>
            </div>
          </div>

          <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
            <h3 className="text-lg font-bold mb-4">All Activity Types</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Object.entries(analytics.activity.by_action || {}).map(([action, count]) => (
                <div key={action} className="bg-gray-800 rounded-lg p-4">
                  <p className="text-2xl font-bold">{count}</p>
                  <p className="text-gray-400 text-sm capitalize">{action.replace('_', ' ')}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Invoices / Purchase history modal */}
      {invoicesModalUser && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setInvoicesModalUser(null)}
        >
          <div
            className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 border-b border-gray-800 flex items-center justify-between sticky top-0 bg-gray-900 z-10">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-500/20 rounded-lg">
                  <FileText className="w-5 h-5 text-purple-300" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">Purchase History</h3>
                  <p className="text-xs text-gray-400">
                    {invoicesModalUser.name || invoicesModalUser.email}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setInvoicesModalUser(null)}
                className="p-2 hover:bg-gray-800 rounded-lg"
              >
                <X size={18} className="text-gray-400" />
              </button>
            </div>

            <div className="p-5">
              {invoicesLoading ? (
                <div className="text-center py-10 text-gray-400 text-sm">Loading invoices…</div>
              ) : !invoicesData?.invoices?.length ? (
                <div className="text-center py-10 text-gray-500 text-sm">
                  No purchase records found for this user.
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Totals row */}
                  <div className="flex items-center justify-between p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                    <span className="text-sm text-gray-300">
                      {invoicesData.count} invoice{invoicesData.count === 1 ? '' : 's'} total
                    </span>
                    <span className="text-lg font-bold text-green-400">
                      ${(invoicesData.total_paid || 0).toFixed(2)}
                    </span>
                  </div>

                  {invoicesData.invoices.map((inv, i) => (
                    <div
                      key={inv.transaction_id || i}
                      className="bg-gray-800/60 border border-gray-700 rounded-lg p-4"
                    >
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-white capitalize">
                              {inv.plan || 'Membership'}
                            </span>
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                              inv.status === 'active'
                                ? 'bg-emerald-500/20 text-emerald-300'
                                : 'bg-gray-700 text-gray-300'
                            }`}>
                              {inv.status || 'unknown'}
                            </span>
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {inv.created_at ? new Date(inv.created_at).toLocaleString() : '—'}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-bold text-green-400">
                            ${(inv.amount || 0).toFixed(2)} {inv.currency || 'USD'}
                          </div>
                          <div className="text-[11px] text-gray-500">
                            {inv.payment_method || 'manual'}
                          </div>
                        </div>
                      </div>
                      {inv.transaction_id && (
                        <div className="text-[11px] text-gray-400 mt-2 font-mono break-all">
                          Txn: {inv.transaction_id}
                        </div>
                      )}
                      {inv.notes && (
                        <div className="text-xs text-gray-300 mt-2 border-t border-gray-700 pt-2">
                          {inv.notes}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal — full form for editing ALL user details */}
      {editingUser && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setEditingUser(null)}
        >
          <div
            className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 border-b border-gray-800 flex items-center justify-between sticky top-0 bg-gray-900 z-10">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-500/20 rounded-lg">
                  <Edit2 className="w-5 h-5 text-purple-300" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">Edit User</h3>
                  <p className="text-xs text-gray-400">{editingUser.email}</p>
                </div>
              </div>
              <button
                onClick={() => setEditingUser(null)}
                className="p-2 hover:bg-gray-800 rounded-lg"
              >
                <X size={18} className="text-gray-400" />
              </button>
            </div>

            <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Name */}
              <div>
                <label className="block text-xs font-semibold text-gray-400 mb-1">Name</label>
                <input
                  type="text"
                  value={editUserForm.name || ''}
                  onChange={(e) => setEditUserForm(f => ({ ...f, name: e.target.value }))}
                  className="w-full px-3 py-2 bg-black border border-gray-700 rounded-lg text-white text-sm focus:border-purple-500 focus:outline-none"
                  data-testid="edit-user-name"
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-xs font-semibold text-gray-400 mb-1">Email</label>
                <input
                  type="email"
                  value={editUserForm.email || ''}
                  onChange={(e) => setEditUserForm(f => ({ ...f, email: e.target.value }))}
                  className="w-full px-3 py-2 bg-black border border-gray-700 rounded-lg text-white text-sm focus:border-purple-500 focus:outline-none"
                  data-testid="edit-user-email"
                />
              </div>

              {/* Password — only updated if filled */}
              <div className="md:col-span-2">
                <label className="block text-xs font-semibold text-gray-400 mb-1">
                  New Password <span className="text-gray-600">(leave blank to keep current)</span>
                </label>
                <input
                  type="text"
                  value={editUserForm.password || ''}
                  onChange={(e) => setEditUserForm(f => ({ ...f, password: e.target.value }))}
                  placeholder="Enter a new password to reset…"
                  className="w-full px-3 py-2 bg-black border border-gray-700 rounded-lg text-white text-sm focus:border-purple-500 focus:outline-none font-mono"
                  data-testid="edit-user-password"
                />
              </div>

              {/* Phone */}
              <div>
                <label className="block text-xs font-semibold text-gray-400 mb-1">Phone</label>
                <input
                  type="text"
                  value={editUserForm.phone || ''}
                  onChange={(e) => setEditUserForm(f => ({ ...f, phone: e.target.value }))}
                  className="w-full px-3 py-2 bg-black border border-gray-700 rounded-lg text-white text-sm focus:border-purple-500 focus:outline-none"
                  data-testid="edit-user-phone"
                />
              </div>

              {/* Company */}
              <div>
                <label className="block text-xs font-semibold text-gray-400 mb-1">Company</label>
                <input
                  type="text"
                  value={editUserForm.company || ''}
                  onChange={(e) => setEditUserForm(f => ({ ...f, company: e.target.value }))}
                  className="w-full px-3 py-2 bg-black border border-gray-700 rounded-lg text-white text-sm focus:border-purple-500 focus:outline-none"
                  data-testid="edit-user-company"
                />
              </div>

              {/* Role */}
              <div>
                <label className="block text-xs font-semibold text-gray-400 mb-1">Role</label>
                <input
                  type="text"
                  value={editUserForm.role || ''}
                  onChange={(e) => setEditUserForm(f => ({ ...f, role: e.target.value }))}
                  placeholder="e.g. CEO, Manager"
                  className="w-full px-3 py-2 bg-black border border-gray-700 rounded-lg text-white text-sm focus:border-purple-500 focus:outline-none"
                  data-testid="edit-user-role"
                />
              </div>

              {/* Industry */}
              <div>
                <label className="block text-xs font-semibold text-gray-400 mb-1">Industry Template</label>
                <select
                  value={editUserForm.industry_template_name || ''}
                  onChange={(e) => setEditUserForm(f => ({ ...f, industry_template_name: e.target.value }))}
                  className="w-full px-3 py-2 bg-black border border-gray-700 rounded-lg text-white text-sm focus:border-purple-500 focus:outline-none"
                  data-testid="edit-user-industry"
                >
                  <option value="">None</option>
                  <option value="Content Creator">Content Creator</option>
                  <option value="Real Estate Agent">Real Estate Agent</option>
                  <option value="Music Producer">Music Producer</option>
                  <option value="Videographer">Videographer</option>
                  <option value="Photographer">Photographer</option>
                  <option value="Small Business">Small Business</option>
                  <option value="Entrepreneur">Entrepreneur</option>
                  <option value="Freelancer">Freelancer</option>
                </select>
              </div>

              {/* Membership plan */}
              <div>
                <label className="block text-xs font-semibold text-gray-400 mb-1">Membership Plan</label>
                <select
                  value={editUserForm.membership_plan || 'free'}
                  onChange={(e) => {
                    const plan = e.target.value;
                    const planSetupDefaults = { personal: 500, pro: 8000, business: 35000, enterprise: 80000, free: 0 };
                    setEditUserForm(f => ({
                      ...f,
                      membership_plan: plan,
                      // Auto-populate setup fee when admin switches plan AND current value
                      // matches a previous default (so manual overrides are preserved).
                      setup_fee: Object.values(planSetupDefaults).some(v => v === parseFloat(f.setup_fee))
                        ? String(planSetupDefaults[plan] ?? 0)
                        : f.setup_fee,
                    }));
                  }}
                  className="w-full px-3 py-2 bg-black border border-gray-700 rounded-lg text-white text-sm focus:border-purple-500 focus:outline-none"
                  data-testid="edit-user-membership"
                >
                  <option value="free">Free</option>
                  <option value="personal">Personal OS</option>
                  <option value="pro">Pro OS</option>
                  <option value="business">Business OS</option>
                  <option value="enterprise">Enterprise OS</option>
                </select>
              </div>

              {/* Setup Fee */}
              <div>
                <label className="block text-xs font-semibold text-gray-400 mb-1">
                  Setup Fee ($) <span className="text-gray-600">(auto-filled by plan)</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={editUserForm.setup_fee ?? '0'}
                  onChange={(e) => setEditUserForm(f => ({ ...f, setup_fee: e.target.value }))}
                  className="w-full px-3 py-2 bg-black border border-gray-700 rounded-lg text-white text-sm focus:border-purple-500 focus:outline-none"
                  data-testid="edit-user-setup-fee"
                />
              </div>

              {/* Total paid */}
              <div>
                <label className="block text-xs font-semibold text-gray-400 mb-1">Total Paid ($)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={editUserForm.total_paid || '0'}
                  onChange={(e) => setEditUserForm(f => ({ ...f, total_paid: e.target.value }))}
                  className="w-full px-3 py-2 bg-black border border-gray-700 rounded-lg text-white text-sm focus:border-purple-500 focus:outline-none"
                  data-testid="edit-user-total-paid"
                />
              </div>

              {/* Last Login */}
              <div className="md:col-span-2">
                <label className="block text-xs font-semibold text-gray-400 mb-1">
                  Last Login <span className="text-gray-600">(leave blank to clear)</span>
                </label>
                <input
                  type="datetime-local"
                  value={editUserForm.last_login || ''}
                  onChange={(e) => setEditUserForm(f => ({ ...f, last_login: e.target.value }))}
                  className="w-full px-3 py-2 bg-black border border-gray-700 rounded-lg text-white text-sm focus:border-purple-500 focus:outline-none"
                  data-testid="edit-user-last-login"
                />
              </div>

              {/* Custom Invoice File Upload */}
              <div className="md:col-span-2">
                <label className="block text-xs font-semibold text-gray-400 mb-1">
                  Custom Invoice File <span className="text-gray-600">(PDF / image — replaces auto-generated mock)</span>
                </label>
                <input
                  type="file"
                  accept=".pdf,image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    if (file.size > 8 * 1024 * 1024) {
                      toast.error('File too large — keep it under 8MB');
                      return;
                    }
                    const reader = new FileReader();
                    reader.onload = () => {
                      setEditUserForm(f => ({
                        ...f,
                        invoice_file: reader.result, // base64 data URL
                        invoice_filename: file.name,
                        invoice_filetype: file.type,
                      }));
                    };
                    reader.readAsDataURL(file);
                  }}
                  className="w-full px-3 py-2 bg-black border border-gray-700 rounded-lg text-white text-sm file:mr-3 file:py-1 file:px-3 file:rounded file:border-0 file:bg-purple-500/20 file:text-purple-300 hover:file:bg-purple-500/30"
                  data-testid="edit-user-invoice-file"
                />
                {editUserForm.invoice_filename && (
                  <div className="mt-2 flex items-center justify-between gap-2 px-3 py-2 bg-purple-500/10 border border-purple-500/30 rounded-md">
                    <div className="flex items-center gap-2 text-xs text-purple-200 min-w-0">
                      <FileText size={14} className="flex-shrink-0" />
                      <span className="truncate">{editUserForm.invoice_filename}</span>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {editUserForm.invoice_file && (
                        <a
                          href={editUserForm.invoice_file}
                          download={editUserForm.invoice_filename}
                          className="text-xs text-purple-300 hover:text-white underline"
                        >
                          Preview
                        </a>
                      )}
                      <button
                        type="button"
                        onClick={() => setEditUserForm(f => ({ ...f, invoice_file: '', invoice_filename: '', invoice_filetype: '' }))}
                        className="text-xs text-red-400 hover:text-red-300"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="p-4 border-t border-gray-800 flex justify-end gap-2 bg-gray-900 sticky bottom-0">
              <button
                onClick={() => setEditingUser(null)}
                className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-gray-200 rounded-lg text-sm"
              >
                Cancel
              </button>
              <button
                onClick={saveFullUserEdit}
                disabled={savingFullEdit}
                className="px-5 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium disabled:opacity-50 inline-flex items-center gap-2"
                data-testid="edit-user-save"
              >
                {savingFullEdit ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete User Confirmation Modal */}
      {showDeleteModal && userToDelete && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-md">
            <div className="p-6 border-b border-gray-700">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-red-500/20 rounded-lg">
                  <Trash2 className="w-6 h-6 text-red-400" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Delete User</h3>
                  <p className="text-sm text-gray-400">This action cannot be undone</p>
                </div>
              </div>
            </div>
            <div className="p-6">
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-4">
                <p className="text-red-300 text-sm">
                  <strong>Warning:</strong> This will permanently delete the user and all their associated data including:
                </p>
                <ul className="text-red-300 text-sm mt-2 list-disc list-inside space-y-1">
                  <li>Account & profile information</li>
                  <li>Tasks, notes, and contacts</li>
                  <li>Media uploads and calendar events</li>
                  <li>Membership and payment records</li>
                </ul>
              </div>
              <div className="bg-gray-800 rounded-lg p-4">
                <p className="text-gray-400 text-sm">User to be deleted:</p>
                <p className="text-white font-semibold">{userToDelete.name || 'Unknown'}</p>
                <p className="text-blue-400 text-sm">{userToDelete.email}</p>
              </div>
            </div>
            <div className="p-4 border-t border-gray-700 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setUserToDelete(null);
                }}
                className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteUser}
                disabled={isDeleting}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors flex items-center gap-2"
                data-testid="confirm-delete-user"
              >
                {isDeleting ? (
                  <>
                    <RefreshCw size={16} className="animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 size={16} />
                    Delete User
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Mock User Modal */}
      {showAddUserModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-lg">
            <div className="p-6 border-b border-gray-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-500/20 rounded-lg">
                    <UserPlus className="w-6 h-6 text-purple-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">Add Mock User</h3>
                    <p className="text-sm text-gray-400">Create a test account for demo purposes</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowAddUserModal(false)}
                  className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
                  data-testid="close-add-user-modal"
                >
                  <X size={20} className="text-gray-400" />
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              {/* Name Field */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">Name *</label>
                <input
                  type="text"
                  value={newUserForm.name}
                  onChange={(e) => setNewUserForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="John Doe"
                  className="w-full px-4 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors"
                  data-testid="mock-user-name-input"
                />
              </div>

              {/* Email Field */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">Email *</label>
                <input
                  type="email"
                  value={newUserForm.email}
                  onChange={(e) => setNewUserForm(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="john@example.com"
                  className="w-full px-4 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors"
                  data-testid="mock-user-email-input"
                />
              </div>

              {/* Password Field */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">Password (optional)</label>
                <input
                  type="text"
                  value={newUserForm.password}
                  onChange={(e) => setNewUserForm(prev => ({ ...prev, password: e.target.value }))}
                  placeholder="Enter password for login access"
                  className="w-full px-4 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors"
                  data-testid="mock-user-password-input"
                />
                <p className="text-xs text-gray-500 mt-1">If provided, this user can log in with these credentials</p>
              </div>

              {/* Two Column Row: Membership & Total Paid */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1.5">Membership Plan</label>
                  <select
                    value={newUserForm.membership_plan}
                    onChange={(e) => setNewUserForm(prev => ({ ...prev, membership_plan: e.target.value }))}
                    className="w-full px-4 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500 transition-colors"
                    data-testid="mock-user-membership-select"
                  >
                    <option value="free">Free</option>
                    <option value="personal">Personal OS</option>
                    <option value="pro">Pro OS</option>
                    <option value="business">Business OS</option>
                    <option value="enterprise">Enterprise OS</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1.5">Total Paid ($)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={newUserForm.total_paid}
                    onChange={(e) => setNewUserForm(prev => ({ ...prev, total_paid: parseFloat(e.target.value) || 0 }))}
                    placeholder="0.00"
                    className="w-full px-4 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors"
                    data-testid="mock-user-total-paid-input"
                  />
                </div>
              </div>

              {/* Two Column Row: Role & Industry */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1.5">Role</label>
                  <input
                    type="text"
                    value={newUserForm.role}
                    onChange={(e) => setNewUserForm(prev => ({ ...prev, role: e.target.value }))}
                    placeholder="Manager, Agent, etc."
                    className="w-full px-4 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors"
                    data-testid="mock-user-role-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1.5">Industry Template</label>
                  <input
                    type="text"
                    value={newUserForm.industry_template_name}
                    onChange={(e) => setNewUserForm(prev => ({ ...prev, industry_template_name: e.target.value }))}
                    placeholder="Real Estate, Music, etc."
                    className="w-full px-4 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors"
                    data-testid="mock-user-industry-input"
                  />
                </div>
              </div>

              {/* Admin Toggle */}
              <div className="flex items-center justify-between p-3 bg-gray-800 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-gray-300">Admin Access</p>
                  <p className="text-xs text-gray-500">Grant admin privileges to this user</p>
                </div>
                <button
                  type="button"
                  onClick={() => setNewUserForm(prev => ({ ...prev, is_admin: !prev.is_admin }))}
                  className={`relative w-12 h-6 rounded-full transition-colors ${newUserForm.is_admin ? 'bg-purple-600' : 'bg-gray-700'}`}
                  data-testid="mock-user-admin-toggle"
                >
                  <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${newUserForm.is_admin ? 'translate-x-7' : 'translate-x-1'}`} />
                </button>
              </div>

              {/* Info Note */}
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                <p className="text-blue-300 text-xs">
                  <strong>Tip:</strong> Add a password to allow this mock user to log in. Without a password, the user appears in the system but cannot authenticate.
                </p>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-gray-700 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowAddUserModal(false);
                  setNewUserForm({
                    name: '',
                    email: '',
                    password: '',
                    membership_plan: 'free',
                    total_paid: 0,
                    role: '',
                    industry_template_name: '',
                    is_admin: false
                  });
                }}
                className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
                disabled={creatingUser}
                data-testid="cancel-add-user"
              >
                Cancel
              </button>
              <button
                onClick={createMockUser}
                disabled={creatingUser || !newUserForm.name.trim() || !newUserForm.email.trim()}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-lg transition-colors flex items-center gap-2"
                data-testid="submit-add-user"
              >
                {creatingUser ? (
                  <>
                    <RefreshCw size={16} className="animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <UserPlus size={16} />
                    Create User
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── TEAMS TAB ── */}
      {activeTab === 'teams' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-white">Team Management</h2>
              <p className="text-gray-400 text-sm mt-1">Small Business (10 users / +$50) and Enterprise (25 users / +$40) teams.</p>
            </div>
            <button onClick={() => setShowNewTeamModal(true)} data-testid="create-team-btn"
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium">
              <Plus size={16} /> New Team
            </button>
          </div>

          {/* Plan legend */}
          <div className="flex gap-3 flex-wrap">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-500/10 border border-blue-500/25 rounded-lg">
              <div className="w-2 h-2 rounded-full bg-blue-400" />
              <span className="text-blue-300 text-xs font-medium">Small Business — $4,000/mo · 10 users included · +$50/extra</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-500/10 border border-amber-500/25 rounded-lg">
              <div className="w-2 h-2 rounded-full bg-amber-400" />
              <span className="text-amber-300 text-xs font-medium">Enterprise — $10,000/mo · 25 users included · +$40/extra</span>
            </div>
          </div>

          {orgTeamsLoading ? (
            <div className="flex items-center justify-center py-12"><RefreshCw className="animate-spin text-gray-400" size={24} /></div>
          ) : orgTeams.length === 0 ? (
            <div className="text-center py-16 border border-dashed border-gray-700 rounded-xl">
              <UsersRound size={40} className="mx-auto text-gray-600 mb-3" />
              <p className="text-gray-400">No teams yet. Create one for a Small Business or Enterprise user.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {orgTeams.map(team => {
                const isExpanded = expandedTeam === team.team_id;
                const included = team.included_users || 10;
                const extra = Math.max(0, (team.members || []).length - included);
                const extraCost = extra * (team.per_extra_user_price || 50);
                const isSmallBiz = team.plan_tier === 'smallbiz';
                return (
                  <div key={team.team_id} className={`border rounded-xl overflow-hidden transition-all ${isSmallBiz ? 'border-blue-500/30' : 'border-amber-500/30'}`}>
                    {/* Team Header */}
                    <div
                      className={`flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-gray-800/40 transition-colors ${isSmallBiz ? 'bg-blue-500/5' : 'bg-amber-500/5'}`}
                      onClick={() => setExpandedTeam(isExpanded ? null : team.team_id)}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold text-sm ${isSmallBiz ? 'bg-blue-500/20 text-blue-400' : 'bg-amber-500/20 text-amber-400'}`}>
                          {(team.name || 'T')[0].toUpperCase()}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="text-white font-semibold">{team.name}</h3>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${isSmallBiz ? 'bg-blue-500/20 text-blue-400' : 'bg-amber-500/20 text-amber-400'}`}>
                              {isSmallBiz ? 'Small Business' : 'Enterprise'}
                            </span>
                          </div>
                          <p className="text-gray-400 text-xs mt-0.5">Owner: {team.owner_email} · {(team.members || []).length}/{included} users{extra > 0 ? ` · +$${extraCost}/mo extra` : ''}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2">
                          <span className="text-gray-400 text-sm">{(team.members || []).length} members</span>
                          {extra > 0 && <span className="px-2 py-0.5 bg-orange-500/20 text-orange-400 text-xs rounded-full border border-orange-500/30">+{extra} extra</span>}
                        </div>
                        <button onClick={(e) => { e.stopPropagation(); deleteOrgTeam(team.team_id, team.name); }}
                          className="p-1.5 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors">
                          <Trash2 size={14} />
                        </button>
                        <ChevronRight size={16} className={`text-gray-500 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                      </div>
                    </div>

                    {/* Expanded: Members List */}
                    {isExpanded && (
                      <div className="p-5 border-t border-gray-800 bg-gray-900/50 space-y-4">
                        {/* Member list */}
                        <div className="space-y-2">
                          {(team.members || []).map((member, idx) => (
                            <div key={member.email} className="flex items-center justify-between px-4 py-3 bg-gray-800 rounded-xl">
                              <div className="flex items-center gap-3">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${member.role === 'owner' ? 'bg-purple-500/30 text-purple-300' : 'bg-gray-700 text-gray-400'}`}>
                                  {(member.name || member.email || '?')[0].toUpperCase()}
                                </div>
                                <div>
                                  <p className="text-sm text-white">{member.name || member.email}</p>
                                  <p className="text-xs text-gray-400">{member.email}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-3">
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${member.role === 'owner' ? 'bg-purple-500/20 text-purple-400' : idx < included ? 'bg-green-500/20 text-green-400' : 'bg-orange-500/20 text-orange-400'}`}>
                                  {member.role === 'owner' ? 'Owner' : idx < included ? 'Member' : 'Extra (+$' + (team.per_extra_user_price || 50) + '/mo)'}
                                </span>
                                {member.role !== 'owner' && (
                                  <button onClick={() => removeTeamMember(team.team_id, member.email)}
                                    className="p-1.5 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors">
                                    <X size={13} />
                                  </button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Add member input */}
                        <div className="flex gap-2 pt-1">
                          <input
                            value={newMemberEmail[team.team_id] || ''}
                            onChange={e => setNewMemberEmail(prev => ({ ...prev, [team.team_id]: e.target.value }))}
                            onKeyDown={e => e.key === 'Enter' && addTeamMember(team.team_id)}
                            placeholder="Add member by email address…"
                            className="flex-1 px-4 py-2.5 bg-black border border-gray-600 rounded-xl text-white text-sm placeholder-gray-600 focus:outline-none focus:border-blue-500"
                          />
                          <button
                            onClick={() => addTeamMember(team.team_id)}
                            disabled={addingMember === team.team_id}
                            className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-xl text-sm font-medium flex items-center gap-2"
                          >
                            {addingMember === team.team_id ? <RefreshCw size={14} className="animate-spin" /> : <UserPlus size={14} />}
                            Add
                          </button>
                        </div>

                        {/* Billing summary */}
                        <div className={`flex items-center justify-between px-4 py-3 rounded-xl border text-sm ${isSmallBiz ? 'border-blue-500/25 bg-blue-500/5' : 'border-amber-500/25 bg-amber-500/5'}`}>
                          <span className="text-gray-400">Monthly billing estimate</span>
                          <div className="flex items-center gap-3">
                            <span className="text-gray-400">{isSmallBiz ? '$4,000' : '$10,000'} base</span>
                            {extra > 0 && <>
                              <span className="text-gray-600">+</span>
                              <span className="text-orange-400">${extraCost} ({extra} extra users)</span>
                            </>}
                            <span className={`font-bold ${isSmallBiz ? 'text-blue-300' : 'text-amber-300'}`}>
                              = ${(isSmallBiz ? 4000 : 10000) + extraCost}/mo
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* New Team Modal */}
          {showNewTeamModal && (
            <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowNewTeamModal(false)}>
              <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-md" onClick={e => e.stopPropagation()}>
                <div className="p-5 border-b border-gray-700 flex items-center justify-between">
                  <h3 className="text-white font-semibold">Create New Team</h3>
                  <button onClick={() => setShowNewTeamModal(false)} className="text-gray-400 hover:text-white"><X size={18} /></button>
                </div>
                <div className="p-5 space-y-4">
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">Team Name</label>
                    <input value={newTeamForm.name} onChange={e => setNewTeamForm(p => ({...p, name: e.target.value}))} placeholder="e.g. Acme Corp" className="w-full px-3 py-2 bg-black border border-gray-600 rounded-lg text-white text-sm" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">Owner Email (must be a registered user)</label>
                    <input value={newTeamForm.owner_email} onChange={e => setNewTeamForm(p => ({...p, owner_email: e.target.value}))} placeholder="owner@company.com" className="w-full px-3 py-2 bg-black border border-gray-600 rounded-lg text-white text-sm" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 mb-2 block">Plan Tier</label>
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        { id: 'smallbiz', label: 'Small Business', sub: '$4,000/mo · 10 users', color: 'blue' },
                        { id: 'enterprise', label: 'Enterprise', sub: '$10,000/mo · 25 users', color: 'amber' }
                      ].map(tier => (
                        <button key={tier.id} onClick={() => setNewTeamForm(p => ({...p, plan_tier: tier.id}))}
                          className={`p-3 rounded-xl border text-left transition-all ${newTeamForm.plan_tier === tier.id
                            ? tier.color === 'blue' ? 'border-blue-500 bg-blue-500/15' : 'border-amber-500 bg-amber-500/15'
                            : 'border-gray-700 hover:border-gray-600'}`}>
                          <p className={`font-semibold text-sm ${tier.color === 'blue' ? 'text-blue-300' : 'text-amber-300'}`}>{tier.label}</p>
                          <p className="text-gray-400 text-xs mt-0.5">{tier.sub}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="p-5 border-t border-gray-700 flex gap-3">
                  <button onClick={() => setShowNewTeamModal(false)} className="flex-1 py-2.5 bg-gray-800 text-gray-300 rounded-lg text-sm">Cancel</button>
                  <button onClick={createOrgTeam} disabled={creatingTeam} className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg text-sm font-semibold flex items-center justify-center gap-2">
                    {creatingTeam ? <RefreshCw size={14} className="animate-spin" /> : <Plus size={14} />} Create Team
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── ADD-ONS TAB ── */}
      {activeTab === 'addons' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-white">Add-on Pages</h2>
              <p className="text-gray-400 text-sm mt-1">Mark pages as add-ons and set monthly pricing. Admin users always have free access.</p>
            </div>
            <button onClick={() => setShowNewAddon(true)} data-testid="create-addon-btn"
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium">
              <Plus size={16} /> Add Page
            </button>
          </div>

          {addonsLoading ? (
            <div className="flex items-center justify-center py-12"><RefreshCw className="animate-spin text-gray-400" size={24} /></div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {addonPages.map(page => (
                <div key={page.page_key} className="bg-gray-900 border border-gray-800 rounded-xl p-5 hover:border-gray-700 transition-colors">
                  {editingAddon === page.page_key ? (
                    <AddonEditForm page={page} onSave={(updates) => saveAddonPage(page.page_key, updates)} onCancel={() => setEditingAddon(null)} />
                  ) : (
                    <>
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${page.color}20` }}>
                            <Package size={18} style={{ color: page.color }} />
                          </div>
                          <div>
                            <h3 className="text-white font-semibold text-sm">{page.label}</h3>
                            <p className="text-gray-500 text-xs">{page.route}</p>
                          </div>
                        </div>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${page.is_addon ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' : 'bg-green-500/20 text-green-400 border border-green-500/30'}`}>
                          {page.is_addon ? 'Add-on' : 'Free'}
                        </span>
                      </div>
                      <p className="text-gray-400 text-xs mb-3 line-clamp-2">{page.description}</p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-white font-bold text-lg">${page.price_monthly}</span>
                          <span className="text-gray-500 text-xs">/mo</span>
                          <span className="text-gray-600 text-xs bg-gray-800 px-2 py-0.5 rounded">{page.category}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <button onClick={() => setEditingAddon(page.page_key)} className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors" title="Edit">
                            <Edit2 size={14} />
                          </button>
                          <button onClick={() => deleteAddonPage(page.page_key)} className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors" title="Delete">
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Create new addon modal */}
          {showNewAddon && (
            <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowNewAddon(false)}>
              <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-lg" onClick={e => e.stopPropagation()}>
                <div className="p-5 border-b border-gray-700 flex items-center justify-between">
                  <h3 className="text-white font-semibold">Create Add-on Page</h3>
                  <button onClick={() => setShowNewAddon(false)} className="text-gray-400 hover:text-white"><X size={18} /></button>
                </div>
                <div className="p-5 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-gray-400 mb-1 block">Page Key (unique)</label>
                      <input value={newAddon.page_key} onChange={e => setNewAddon(p => ({...p, page_key: e.target.value}))} placeholder="e.g. travel" className="w-full px-3 py-2 bg-black border border-gray-600 rounded-lg text-white text-sm" />
                    </div>
                    <div>
                      <label className="text-xs text-gray-400 mb-1 block">Label</label>
                      <input value={newAddon.label} onChange={e => setNewAddon(p => ({...p, label: e.target.value}))} placeholder="e.g. Travel" className="w-full px-3 py-2 bg-black border border-gray-600 rounded-lg text-white text-sm" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-gray-400 mb-1 block">Route</label>
                      <input value={newAddon.route} onChange={e => setNewAddon(p => ({...p, route: e.target.value}))} placeholder="/travel" className="w-full px-3 py-2 bg-black border border-gray-600 rounded-lg text-white text-sm" />
                    </div>
                    <div>
                      <label className="text-xs text-gray-400 mb-1 block">Price/month ($)</label>
                      <input type="number" step="0.01" value={newAddon.price_monthly} onChange={e => setNewAddon(p => ({...p, price_monthly: parseFloat(e.target.value)}))} className="w-full px-3 py-2 bg-black border border-gray-600 rounded-lg text-white text-sm" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">Description</label>
                    <textarea value={newAddon.description} onChange={e => setNewAddon(p => ({...p, description: e.target.value}))} rows={2} className="w-full px-3 py-2 bg-black border border-gray-600 rounded-lg text-white text-sm resize-none" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-gray-400 mb-1 block">Category</label>
                      <select value={newAddon.category} onChange={e => setNewAddon(p => ({...p, category: e.target.value}))} className="w-full px-3 py-2 bg-black border border-gray-600 rounded-lg text-white text-sm">
                        {['Workspace','Lifestyle','CRM','Files','Team','Marketing','Accounting'].map(c => <option key={c}>{c}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-gray-400 mb-1 block">Color</label>
                      <input type="color" value={newAddon.color} onChange={e => setNewAddon(p => ({...p, color: e.target.value}))} className="w-full h-10 bg-black border border-gray-600 rounded-lg cursor-pointer" />
                    </div>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={newAddon.is_addon} onChange={e => setNewAddon(p => ({...p, is_addon: e.target.checked}))} className="w-4 h-4 rounded" />
                    <span className="text-sm text-gray-300">Mark as add-on (requires subscription for regular users)</span>
                  </label>
                </div>
                <div className="p-5 border-t border-gray-700 flex gap-3">
                  <button onClick={() => setShowNewAddon(false)} className="flex-1 py-2 bg-gray-800 text-gray-300 rounded-lg text-sm">Cancel</button>
                  <button onClick={createAddonPage} className="flex-1 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium">Create Page</button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── TEMPLATES TAB ── */}
      {activeTab === 'templates' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-white">Industry Templates</h2>
              <p className="text-gray-400 text-sm mt-1">Define page bundles for different industries. New users pick a template during onboarding.</p>
            </div>
            <button onClick={() => setShowNewTemplate(true)} data-testid="create-template-btn"
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium">
              <Plus size={16} /> New Template
            </button>
          </div>

          {templatesLoading ? (
            <div className="flex items-center justify-center py-12"><RefreshCw className="animate-spin text-gray-400" size={24} /></div>
          ) : (
            <div className="space-y-10">
              {['Personal', 'Small Business', 'Enterprise'].map(cat => {
                const catTemplates = industryTemplates.filter(t => (t.category || 'Small Business') === cat);
                const catColors = { Personal: '#8B5CF6', 'Small Business': '#0EA5E9', Enterprise: '#10B981' };
                return (
                  <div key={cat}>
                    {/* Category header */}
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: catColors[cat] }} />
                      <h3 className="text-white font-bold text-base">{cat}</h3>
                      <span className="text-xs text-gray-600 bg-gray-800 px-2 py-0.5 rounded-full">{catTemplates.length}</span>
                      <div className="flex-1 h-px bg-gray-800" />
                      {cat !== 'Personal' && (
                        <button
                          onClick={() => {
                            setNewTemplate({ id: '', name: '', description: '', category: cat, core_pages: [], addon_pages: [], roles: [], color: catColors[cat] || '#6B7280' });
                            setShowNewTemplate(true);
                          }}
                          className="flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-lg border transition-colors"
                          style={{ borderColor: `${catColors[cat]}60`, color: catColors[cat], backgroundColor: `${catColors[cat]}10` }}
                          data-testid={`add-company-${cat.replace(' ', '-').toLowerCase()}-btn`}
                        >
                          <Plus size={12} /> Add Company
                        </button>
                      )}
                    </div>

                    {catTemplates.length === 0 ? (
                      <div className="border border-dashed border-gray-800 rounded-xl py-8 text-center text-gray-600 text-sm">
                        No {cat} templates yet — create one with the "New Template" button
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                        {catTemplates.map(tmpl => (
                          <div key={tmpl.id} className="bg-gray-900 border border-gray-800 rounded-xl p-5 hover:border-gray-700 transition-colors">
                            {editingTemplate === tmpl.id ? (
                              <TemplateEditForm template={tmpl} allAddonPages={addonPages} onSave={(updates) => saveTemplate(tmpl.id, updates)} onCancel={() => setEditingTemplate(null)} />
                            ) : (
                              <>
                                <div className="flex items-start justify-between mb-3">
                                  <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${tmpl.color}20` }}>
                                      <Sparkles size={18} style={{ color: tmpl.color }} />
                                    </div>
                                    <div>
                                      <h3 className="text-white font-semibold">{tmpl.name}</h3>
                                      <p className="text-gray-500 text-xs">{tmpl.id}</p>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <button onClick={() => { setEditingTemplate(tmpl.id); fetchAddonPages(); }} className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg"><Edit2 size={14} /></button>
                                    <button onClick={() => deleteTemplate(tmpl.id)} className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg"><Trash2 size={14} /></button>
                                  </div>
                                </div>
                                <p className="text-gray-400 text-xs mb-4">{tmpl.description}</p>
                                <div className="space-y-2">
                                  {cat === 'Personal' ? (
                                    /* Personal: Show roles with their core_pages and addon_pages */
                                    <>
                                      <span className="text-xs text-gray-500 uppercase tracking-wider">Roles ({tmpl.roles?.length || 0})</span>
                                      <div className="flex flex-col gap-2 mt-1">
                                        {(tmpl.roles || []).map(role => (
                                          <div key={role.id} className="bg-black/30 rounded-lg p-2.5">
                                            <div className="flex items-center gap-2 mb-2">
                                              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: tmpl.color || '#6B7280' }} />
                                              <span className="text-xs text-gray-200 font-medium flex-1">{role.name}</span>
                                            </div>
                                            <div className="pl-4 space-y-1.5">
                                              {role.core_pages?.length > 0 && (
                                                <div>
                                                  <span className="text-[10px] text-gray-600 uppercase">Core</span>
                                                  <div className="flex flex-wrap gap-1 mt-0.5">
                                                    {role.core_pages.slice(0, 3).map(p => (
                                                      <span key={p} className="px-1.5 py-0.5 bg-green-500/15 text-green-400 rounded text-[10px]">{p.replace('/', '') || 'dashboard'}</span>
                                                    ))}
                                                    {role.core_pages.length > 3 && <span className="text-[10px] text-gray-600">+{role.core_pages.length - 3}</span>}
                                                  </div>
                                                </div>
                                              )}
                                              {role.addon_pages?.length > 0 && (
                                                <div>
                                                  <span className="text-[10px] text-gray-600 uppercase">Add-ons</span>
                                                  <div className="flex flex-wrap gap-1 mt-0.5">
                                                    {role.addon_pages.slice(0, 3).map(p => (
                                                      <span key={p} className="px-1.5 py-0.5 bg-amber-500/15 text-amber-400 rounded text-[10px]">{p.replace('/', '')}</span>
                                                    ))}
                                                    {role.addon_pages.length > 3 && <span className="text-[10px] text-gray-600">+{role.addon_pages.length - 3}</span>}
                                                  </div>
                                                </div>
                                              )}
                                              {(!role.core_pages?.length && !role.addon_pages?.length) && (
                                                <span className="text-[10px] text-gray-600 italic">No pages assigned</span>
                                              )}
                                            </div>
                                          </div>
                                        ))}
                                        {(tmpl.roles || []).length === 0 && (
                                          <span className="text-xs text-gray-600 italic">No roles yet — click edit to add</span>
                                        )}
                                      </div>
                                    </>
                                  ) : (
                                    /* Small Business & Enterprise: Show sub_companies with their roles */
                                    <>
                                      <span className="text-xs text-gray-500 uppercase tracking-wider">Sub-Companies ({tmpl.sub_companies?.length || 0})</span>
                                      <div className="flex flex-col gap-1.5 mt-1">
                                        {(tmpl.sub_companies || []).map(item => (
                                          <div key={item.id} className="bg-black/30 rounded-lg p-2">
                                            <div className="flex items-center gap-2 mb-1">
                                              <div className="w-1.5 h-1.5 rounded-full bg-blue-400/70 flex-shrink-0" />
                                              <span className="text-xs text-gray-200 font-medium flex-1 truncate">{item.name}</span>
                                              <span className="text-xs text-gray-600">{item.roles?.length || 0} roles</span>
                                            </div>
                                            {item.roles?.length > 0 && (
                                              <div className="pl-3 flex flex-wrap gap-1">
                                                {item.roles.slice(0, 2).map(r => (
                                                  <span key={r.id} className="text-xs text-gray-500 bg-gray-800 px-1.5 py-0.5 rounded">{r.name}</span>
                                                ))}
                                                {item.roles.length > 2 && <span className="text-xs text-gray-600">+{item.roles.length - 2}</span>}
                                              </div>
                                            )}
                                          </div>
                                        ))}
                                        {(tmpl.sub_companies || []).length === 0 && (
                                          <span className="text-xs text-gray-600 italic">No sub-companies yet — click edit to add</span>
                                        )}
                                      </div>
                                    </>
                                  )}
                                </div>
                                
                                {/* Demo Button */}
                                <div className="mt-4 pt-3 border-t border-gray-800">
                                  <a
                                    href={`/demo/${tmpl.id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm transition-all hover:scale-[1.02]"
                                    style={{ 
                                      backgroundColor: `${tmpl.color}15`,
                                      color: tmpl.color,
                                      border: `1px solid ${tmpl.color}30`
                                    }}
                                    data-testid={`view-demo-${tmpl.id}-btn`}
                                  >
                                    <Eye size={14} />
                                    View Demo
                                    <ExternalLink size={12} className="opacity-60" />
                                  </a>
                                </div>
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* New Template Modal */}
          {showNewTemplate && (
            <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowNewTemplate(false)}>
              <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-lg" onClick={e => e.stopPropagation()}>
                <div className="p-5 border-b border-gray-700 flex items-center justify-between">
                  <h3 className="text-white font-semibold">
                    {(newTemplate.category === 'Small Business' || newTemplate.category === 'Enterprise') ? `Add Company — ${newTemplate.category}` : 'Create Industry Template'}
                  </h3>
                  <button onClick={() => setShowNewTemplate(false)} className="text-gray-400 hover:text-white"><X size={18} /></button>
                </div>
                <div className="p-5 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-gray-400 mb-1 block">Company ID</label>
                      <input value={newTemplate.id || ''} onChange={e => setNewTemplate(p => ({...p, id: e.target.value}))} placeholder="e.g. healthcare" className="w-full px-3 py-2 bg-black border border-gray-600 rounded-lg text-white text-sm" />
                    </div>
                    <div>
                      <label className="text-xs text-gray-400 mb-1 block">Company Name</label>
                      <input value={newTemplate.name} onChange={e => setNewTemplate(p => ({...p, name: e.target.value}))} placeholder="e.g. Healthcare" className="w-full px-3 py-2 bg-black border border-gray-600 rounded-lg text-white text-sm" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">Category</label>
                    <select value={newTemplate.category || 'Small Business'} onChange={e => setNewTemplate(p => ({...p, category: e.target.value}))}
                      className="w-full px-3 py-2 bg-black border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-purple-500">
                      <option value="Personal" className="bg-gray-900">Personal</option>
                      <option value="Small Business" className="bg-gray-900">Small Business</option>
                      <option value="Enterprise" className="bg-gray-900">Enterprise</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">Description</label>
                    <textarea value={newTemplate.description} onChange={e => setNewTemplate(p => ({...p, description: e.target.value}))} rows={2} className="w-full px-3 py-2 bg-black border border-gray-600 rounded-lg text-white text-sm resize-none" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">Color</label>
                    <input type="color" value={newTemplate.color} onChange={e => setNewTemplate(p => ({...p, color: e.target.value}))} className="w-full h-10 bg-black border border-gray-600 rounded-lg cursor-pointer" />
                  </div>
                  {(newTemplate.category === 'Small Business' || newTemplate.category === 'Enterprise') && (
                    <p className="text-xs text-gray-500 bg-gray-800/60 rounded-lg px-3 py-2">
                      Pages are defined per role — after creating this company type, open it to add roles and assign pages to each.
                    </p>
                  )}
                </div>
                <div className="p-5 border-t border-gray-700 flex gap-3">
                  <button onClick={() => setShowNewTemplate(false)} className="flex-1 py-2 bg-gray-800 text-gray-300 rounded-lg text-sm">Cancel</button>
                  <button onClick={createTemplate} className="flex-1 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium">
                    {(newTemplate.category === 'Small Business' || newTemplate.category === 'Enterprise') ? 'Add Company' : 'Create Template'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'team-chat' && (
        <div className="space-y-6">
          {/* Sub-tabs */}
          <div className="flex gap-2 border-b border-gray-800 pb-0">
            {[{id:'teams', label:'Teams', icon: UsersRound}, {id:'members', label:'Member Visibility', icon: UserCheck}].map(tab => (
              <button key={tab.id} onClick={() => setTeamSubTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${teamSubTab === tab.id ? 'border-purple-500 text-purple-400' : 'border-transparent text-gray-400 hover:text-white'}`}>
                <tab.icon size={14} />{tab.label}
              </button>
            ))}
          </div>

          {/* ── TEAMS TAB ── */}
          {teamSubTab === 'teams' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-white">Chat Teams</h2>
                  <p className="text-gray-400 text-sm mt-1">Create teams and assign members. Teams appear as group chats in the Communications page.</p>
                </div>
                <button onClick={() => setShowCreateTeam(true)} data-testid="create-team-btn"
                  className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium transition-colors">
                  <Plus size={16} /> New Team
                </button>
              </div>

              {/* Create Team Form */}
              {showCreateTeam && (
                <div className="bg-gray-900 border border-purple-500/40 rounded-xl p-5 space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-white font-semibold">Create New Team</h3>
                    <button onClick={() => setShowCreateTeam(false)} className="text-gray-400 hover:text-white"><X size={18} /></button>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-gray-400 mb-1 block">Team Name *</label>
                      <input value={newTeamName} onChange={e => setNewTeamName(e.target.value)}
                        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:border-purple-500 outline-none"
                        placeholder="e.g. Marketing Team" data-testid="team-name-input" />
                    </div>
                    <div>
                      <label className="text-xs text-gray-400 mb-1 block">Color</label>
                      <div className="flex gap-2 flex-wrap">
                        {['purple','blue','green','red','orange','cyan','pink'].map(c => (
                          <button key={c} onClick={() => setNewTeamColor(c)}
                            className={`w-7 h-7 rounded-full border-2 transition-all ${newTeamColor === c ? 'border-white scale-110' : 'border-transparent'} bg-${c}-500`} />
                        ))}
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">Description (optional)</label>
                    <input value={newTeamDesc} onChange={e => setNewTeamDesc(e.target.value)}
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:border-purple-500 outline-none"
                      placeholder="What is this team for?" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 mb-2 block">Select Members ({newTeamMembers.length} selected)</label>
                    <div className="grid grid-cols-2 gap-2 max-h-52 overflow-y-auto">
                      {messagingMembers.map(member => (
                        <button key={member.user_id}
                          onClick={() => toggleTeamMember(member.user_id, newTeamMembers, setNewTeamMembers)}
                          data-testid={`select-member-${member.user_id}`}
                          className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm border transition-all ${newTeamMembers.includes(member.user_id) ? 'border-purple-500 bg-purple-500/20 text-white' : 'border-gray-700 bg-gray-800 text-gray-300 hover:border-gray-500'}`}>
                          <div className="w-6 h-6 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                            {member.name?.charAt(0)?.toUpperCase()}
                          </div>
                          <span className="truncate">{member.name}</span>
                          {newTeamMembers.includes(member.user_id) && <CheckCircle size={14} className="text-purple-400 ml-auto flex-shrink-0" />}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="flex gap-3 pt-2">
                    <button onClick={createTeam} data-testid="save-team-btn"
                      className="flex-1 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium transition-colors">
                      Create Team
                    </button>
                    <button onClick={() => setShowCreateTeam(false)} className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg text-sm transition-colors">
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Teams List */}
              {teamsLoading ? (
                <div className="text-center py-12 text-gray-500">Loading teams...</div>
              ) : teams.length === 0 ? (
                <div className="text-center py-16 border border-dashed border-gray-700 rounded-xl">
                  <UsersRound size={40} className="text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-400 font-medium">No teams yet</p>
                  <p className="text-gray-600 text-sm mt-1">Create your first team to get started</p>
                </div>
              ) : (
                <div className="grid gap-4">
                  {teams.map(team => (
                    <div key={team.team_id} className="bg-gray-900 border border-gray-800 rounded-xl p-5 hover:border-gray-600 transition-colors">
                      {editingTeam === team.team_id ? (
                        /* Inline Edit Form */
                        <EditTeamInline team={team} messagingMembers={messagingMembers}
                          onSave={(updates) => updateTeam(team.team_id, updates)}
                          onCancel={() => setEditingTeam(null)} />
                      ) : (
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex items-center gap-4 flex-1 min-w-0">
                            <div className={`w-12 h-12 rounded-xl bg-${team.color || 'purple'}-500/20 flex items-center justify-center flex-shrink-0`}>
                              <Hash size={22} className={`text-${team.color || 'purple'}-400`} />
                            </div>
                            <div className="min-w-0">
                              <h3 className="text-white font-semibold text-lg">{team.name}</h3>
                              {team.description && <p className="text-gray-400 text-sm mt-0.5">{team.description}</p>}
                              <div className="flex items-center gap-3 mt-2">
                                <span className="text-xs text-gray-500 flex items-center gap-1"><Users size={12} />{team.member_count || team.members?.length || 0} members</span>
                                <span className="text-xs text-gray-500 flex items-center gap-1"><MessageSquare size={12} />{team.message_count || 0} messages</span>
                              </div>
                              {/* Member avatars */}
                              <div className="flex items-center gap-1 mt-3">
                                {(team.members || []).slice(0, 8).map(m => (
                                  <div key={m.user_id} title={m.name}
                                    className="w-7 h-7 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center text-white text-xs font-bold border-2 border-gray-900 -ml-1 first:ml-0 flex-shrink-0">
                                    {m.avatar ? <img src={m.avatar} alt={m.name} className="w-full h-full rounded-full object-cover" /> : m.name?.charAt(0)?.toUpperCase()}
                                  </div>
                                ))}
                                {(team.members?.length || 0) > 8 && (
                                  <span className="text-xs text-gray-400 ml-2">+{team.members.length - 8} more</span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex gap-2 flex-shrink-0">
                            <button onClick={() => setEditingTeam(team.team_id)} data-testid={`edit-team-${team.team_id}`}
                              className="p-2 hover:bg-gray-700 rounded-lg text-gray-400 hover:text-white transition-colors">
                              <Edit2 size={16} />
                            </button>
                            <button onClick={() => deleteTeam(team.team_id, team.name)} data-testid={`delete-team-${team.team_id}`}
                              className="p-2 hover:bg-red-900/30 rounded-lg text-gray-400 hover:text-red-400 transition-colors">
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── MEMBER VISIBILITY TAB ── */}
          {teamSubTab === 'members' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-white">Team Chat Members</h2>
                  <p className="text-gray-400 text-sm mt-1">Control which users appear in the team members list on the Dashboard messaging tab.</p>
                </div>
                <button onClick={fetchMessagingMembers} className="flex items-center gap-2 px-3 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm text-gray-300 transition-colors">
                  <RefreshCw size={14} /> Refresh
                </button>
              </div>
              {messagingLoading ? (
                <div className="text-center py-12 text-gray-500">Loading members...</div>
              ) : (
                <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
                  <div className="grid grid-cols-12 px-4 py-2 border-b border-gray-800 text-xs font-semibold text-gray-500 uppercase">
                    <span className="col-span-4">Name</span><span className="col-span-4">Email</span>
                    <span className="col-span-2">Role</span><span className="col-span-2 text-right">Visible in Chat</span>
                  </div>
                  {messagingMembers.map(member => (
                    <div key={member.user_id} className="grid grid-cols-12 items-center px-4 py-3 border-b border-gray-800 last:border-0 hover:bg-gray-800/40 transition-colors">
                      <div className="col-span-4 flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                          {member.name?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                        <span className="text-white text-sm font-medium truncate">{member.name}</span>
                      </div>
                      <span className="col-span-4 text-gray-400 text-sm truncate pr-4">{member.email}</span>
                      <span className="col-span-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${member.role === 'admin' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-gray-700 text-gray-400'}`}>
                          {member.role || 'user'}
                        </span>
                      </span>
                      <div className="col-span-2 flex justify-end">
                        <button onClick={() => toggleMessagingVisibility(member.user_id)} data-testid={`toggle-messaging-${member.user_id}`}
                          className={`relative w-11 h-6 rounded-full transition-colors duration-200 focus:outline-none ${member.messaging_visible !== false ? 'bg-green-500' : 'bg-gray-600'}`}>
                          <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${member.messaging_visible !== false ? 'translate-x-5' : 'translate-x-0'}`} />
                        </button>
                      </div>
                    </div>
                  ))}
                  {messagingMembers.length === 0 && <div className="text-center py-12 text-gray-500">No users found</div>}
                </div>
              )}
            </div>
          )}
        </div>
      )}

    </div>
  );
};

// Inline team editor component
const EditTeamInline = ({ team, messagingMembers, onSave, onCancel }) => {
  const [name, setName] = useState(team.name);
  const [desc, setDesc] = useState(team.description || '');
  const [color, setColor] = useState(team.color || 'purple');
  const [members, setMembers] = useState(team.member_user_ids || []);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-white font-semibold">Edit Team</span>
        <button onClick={onCancel} className="text-gray-400 hover:text-white"><X size={16} /></button>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-gray-400 mb-1 block">Team Name</label>
          <input value={name} onChange={e => setName(e.target.value)}
            className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:border-purple-500 outline-none" />
        </div>
        <div>
          <label className="text-xs text-gray-400 mb-1 block">Color</label>
          <div className="flex gap-1.5 flex-wrap">
            {['purple','blue','green','red','orange','cyan','pink'].map(c => (
              <button key={c} onClick={() => setColor(c)}
                className={`w-6 h-6 rounded-full border-2 ${color === c ? 'border-white' : 'border-transparent'} bg-${c}-500`} />
            ))}
          </div>
        </div>
      </div>
      <div>
        <label className="text-xs text-gray-400 mb-1 block">Description</label>
        <input value={desc} onChange={e => setDesc(e.target.value)}
          className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:border-purple-500 outline-none" />
      </div>
      <div>
        <label className="text-xs text-gray-400 mb-2 block">Members ({members.length} selected)</label>
        <div className="grid grid-cols-2 gap-1.5 max-h-40 overflow-y-auto">
          {messagingMembers.map(m => (
            <button key={m.user_id}
              onClick={() => setMembers(prev => prev.includes(m.user_id) ? prev.filter(id => id !== m.user_id) : [...prev, m.user_id])}
              className={`flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs border transition-all ${members.includes(m.user_id) ? 'border-purple-500 bg-purple-500/20 text-white' : 'border-gray-700 bg-gray-800 text-gray-400 hover:border-gray-500'}`}>
              <div className="w-5 h-5 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                {m.name?.charAt(0)?.toUpperCase()}
              </div>
              <span className="truncate">{m.name}</span>
            </button>
          ))}
        </div>
      </div>
      <div className="flex gap-2 pt-1">
        <button onClick={() => onSave({ name, description: desc, color, member_user_ids: members })}
          className="flex-1 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium">Save Changes</button>
        <button onClick={onCancel} className="px-4 py-2 bg-gray-800 text-gray-300 rounded-lg text-sm">Cancel</button>
      </div>
    </div>
  );
};

// ─── USER DETAIL PANEL ────────────────────────────────────────────────────────
const ALL_SIDEBAR_PAGES = [
  // WORKSPACE
  { label: 'Dashboard', path: '/', section: 'Workspace', alwaysOn: true },
  { label: 'Communications', path: '/communications-re', section: 'Workspace' },
  { label: 'Contacts', path: '/contacts-re', section: 'Workspace' },
  { label: 'Calendar', path: '/calendar', section: 'Workspace' },
  { label: 'Tasks', path: '/tasks', section: 'Workspace' },
  { label: 'Projects', path: '/projects-re', section: 'Workspace' },
  
  // LIFESTYLE
  { label: 'Travel', path: '/travel', section: 'Lifestyle' },
  { label: 'Tickets', path: '/tickets', section: 'Lifestyle' },
  { label: 'Shopping', path: '/shopping', section: 'Lifestyle' },
  { label: 'Food', path: '/food', section: 'Lifestyle' },
  { label: 'Health', path: '/health', section: 'Lifestyle' },
  { label: 'Smart Home', path: '/security-cameras', section: 'Lifestyle' },
  
  // FILES
  { label: 'Documents', path: '/documents-re', section: 'Files' },
  { label: 'Passwords', path: '/passwords', section: 'Files' },
  
  // MEDIA
  { label: 'Media Portfolio', path: '/media-portfolio-re', section: 'Media' },
  { label: 'Media Factory', path: '/media-factory', section: 'Media' },
  { label: 'Media Releases', path: '/music-releases', section: 'Media' },
  { label: 'Moodboard', path: '/moodboard', section: 'Media' },
  
  // TEAM
  { label: 'Clients & Customers', path: '/clients-customers-re', section: 'Team' },
  { label: 'Employees', path: '/employees-re', section: 'Team' },
  { label: 'HR', path: '/hr', section: 'Team' },
  { label: 'Talent Database', path: '/talent-database', section: 'Team' },
  { label: 'Rosters', path: '/rosters', section: 'Team' },
  
  // OPERATIONS
  { label: 'Orders', path: '/orders', section: 'Operations' },
  { label: 'Bookings', path: '/bookings', section: 'Operations' },
  { label: 'Services', path: '/creative-services', section: 'Operations' },
  { label: 'Inventory', path: '/inventory', section: 'Operations' },
  { label: 'Properties', path: '/properties', section: 'Operations' },
  
  // SALES
  { label: 'Lead Generation', path: '/lead-generation', section: 'Sales' },
  { label: 'Customer Service', path: '/customer-service', section: 'Sales' },
  { label: 'Reviews & Feedback', path: '/reviews-feedback', section: 'Sales' },
  
  // MARKETING
  { label: 'Social Media', path: '/social-media', section: 'Marketing' },
  { label: 'Marketing', path: '/marketing-re', section: 'Marketing' },
  
  // ACCOUNTING
  { label: 'Finances', path: '/finances-re', section: 'Accounting' },
  { label: 'Budgeting', path: '/budgeting', section: 'Accounting' },
  { label: 'Investments', path: '/investments-re', section: 'Accounting' },
  
  // SPORTS
  { label: 'Live Scores', path: '/live-scores', section: 'Sports' },
  { label: 'Game Day Operations', path: '/game-day-operations', section: 'Sports' },
  { label: 'Facilities & Equipment', path: '/facilities-equipment', section: 'Sports' },
  { label: 'Performance', path: '/performance', section: 'Sports' },
  { label: 'Film Analysis', path: '/film-analysis', section: 'Sports' },
  { label: 'Player Health', path: '/player-health', section: 'Sports' },
  { label: 'Scouting', path: '/scouting', section: 'Sports' },
  { label: 'Draft', path: '/draft', section: 'Sports' },
  
  // UTILITIES
  { label: 'Integrations', path: '/my-apps', section: 'Utilities', alwaysOn: true },
];

const PAGE_SECTIONS_LIST = ['Workspace', 'Lifestyle', 'Files', 'Media', 'Team', 'Operations', 'Sales', 'Marketing', 'Accounting', 'Sports', 'Utilities'];

const UserDetailPanel = ({ user, onClose, onDelete, copyToClipboard, getPlanColor, showPasswords, togglePasswordVisibility, apiUrl, adminKey }) => {
  const [detailTab, setDetailTab] = useState('details');
  const [hiddenPages, setHiddenPages] = useState([]);
  const [addons, setAddons] = useState([]);
  const [pagesLoading, setPagesLoading] = useState(false);
  const [savingPages, setSavingPages] = useState(false);

  useEffect(() => {
    if (!user?.user_id) return;
    setPagesLoading(true);
    fetch(`${apiUrl}/api/user-addons/page-settings?user_id=${encodeURIComponent(user.user_id)}`)
      .then(r => r.json())
      .then(data => {
        setHiddenPages(data.hidden_pages || []);
        setAddons(data.addons || []);
      })
      .catch(() => {})
      .finally(() => setPagesLoading(false));
  }, [user?.user_id, apiUrl]);

  const togglePageForUser = async (path) => {
    if (!user?.user_id) return;
    const next = hiddenPages.includes(path)
      ? hiddenPages.filter(p => p !== path)
      : [...hiddenPages, path];
    setHiddenPages(next);
    setSavingPages(true);
    try {
      await fetch(`${apiUrl}/api/user-addons/page-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.user_id, hidden_pages: next })
      });
    } catch (e) { /* ignore */ }
    setSavingPages(false);
  };

  const toggleAllSection = async (section, forceOn) => {
    const sectionPaths = ALL_SIDEBAR_PAGES.filter(p => p.section === section && !p.alwaysOn).map(p => p.path);
    let next;
    if (forceOn) {
      // Show all pages in this section
      next = hiddenPages.filter(p => !sectionPaths.includes(p));
    } else {
      // Hide all pages in this section
      next = [...new Set([...hiddenPages, ...sectionPaths])];
    }
    setHiddenPages(next);
    setSavingPages(true);
    try {
      await fetch(`${apiUrl}/api/user-addons/page-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.user_id, hidden_pages: next })
      });
    } catch { /* ignored */ }
    setSavingPages(false);
  };

  const revokeAddon = async (pageKey, label) => {
    if (!window.confirm(`Revoke ${label} subscription for ${user.email}?`)) return;
    try {
      await fetch(`${apiUrl}/api/user-addons/addons/${pageKey}/admin?user_id=${encodeURIComponent(user.user_id)}&admin_key=${adminKey}`, {
        method: 'DELETE'
      });
      setAddons(prev => prev.filter(a => a.page_key !== pageKey));
    } catch { /* ignored */ }
  };

  const isPageOn = (path) => !hiddenPages.includes(path);
  const visibleCount = ALL_SIDEBAR_PAGES.filter(p => !hiddenPages.includes(p.path)).length;

  return (
    <div className="bg-gray-900 border border-gray-700 rounded-2xl overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 bg-gray-800/60 border-b border-gray-700">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center font-bold text-sm">
            {(user.name || user.email || '?')[0].toUpperCase()}
          </div>
          <div>
            <p className="text-white font-semibold">{user.name || user.email}</p>
            <p className="text-gray-400 text-xs">{user.email} · {user.user_id}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onDelete} className="px-3 py-1.5 bg-red-600/20 hover:bg-red-600/40 text-red-400 text-xs rounded-lg flex items-center gap-1.5 border border-red-600/30 transition-colors">
            <Trash2 size={13} /> Delete
          </button>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg">
            <X size={18} />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-800 px-6">
        {[
          { id: 'details', label: 'Details', icon: Users },
          { id: 'pages', label: `Pages (${visibleCount}/${ALL_SIDEBAR_PAGES.length} on)`, icon: LayoutGrid },
          { id: 'addons', label: `Add-ons (${addons.length})`, icon: Package },
        ].map(({ id, label, icon: Icon }) => (
          <button key={id} onClick={() => setDetailTab(id)}
            className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 transition-all -mb-px ${
              detailTab === id ? 'border-blue-500 text-white' : 'border-transparent text-gray-500 hover:text-gray-300'
            }`}>
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="p-6">

        {/* DETAILS TAB */}
        {detailTab === 'details' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { label: 'Full Name', value: user.name || 'Not set' },
              { label: 'Role', value: user.role || 'Not set' },
              { label: 'Company', value: user.company || 'Not set' },
              { label: 'Auth Provider', value: user.auth_provider || 'email', className: 'capitalize' },
              { label: 'Created At', value: user.created_at ? new Date(user.created_at).toLocaleString() : '-' },
              { label: 'Last Login', value: user.last_login ? new Date(user.last_login).toLocaleString() : 'Never' },
            ].map(({ label, value, className }) => (
              <div key={label} className="bg-gray-800 rounded-lg p-4">
                <label className="text-xs text-gray-500">{label}</label>
                <p className={`font-semibold mt-0.5 text-sm ${className || ''}`}>{value}</p>
              </div>
            ))}
            <div className="bg-gray-800 rounded-lg p-4">
              <label className="text-xs text-gray-500">Membership Plan</label>
              <div className="mt-1">
                <span className={`px-2 py-1 rounded text-xs text-white font-medium ${getPlanColor(user.membership_plan)}`}>
                  {user.membership_plan || 'free'}
                </span>
              </div>
            </div>
            <div className="bg-gray-800 rounded-lg p-4">
              <label className="text-xs text-gray-500">Total Paid</label>
              <p className="text-lg font-bold text-green-400 mt-0.5">${(user.total_paid || 0).toFixed(2)}</p>
            </div>
            <div className="bg-gray-800 rounded-lg p-4">
              <label className="text-xs text-gray-500">Industry</label>
              <div className="mt-1">
                {user.industry_template_name ? (
                  <span className="px-2 py-1 bg-purple-500/20 text-purple-300 text-xs rounded-full border border-purple-500/30 flex items-center gap-1 w-fit">
                    <Sparkles size={10} /> {user.industry_template_name}
                  </span>
                ) : <span className="text-gray-500 text-sm">—</span>}
              </div>
            </div>
            <div className="bg-gray-800 rounded-lg p-4">
              <label className="text-xs text-gray-500">Status</label>
              <div className="mt-1">
                <span className={`px-2 py-1 rounded text-xs font-medium ${user.is_active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                  {user.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
            <div className="bg-gray-800 rounded-lg p-4">
              <label className="text-xs text-gray-500">Password</label>
              <div className="flex items-center gap-2 mt-1">
                {user.plain_password ? (
                  <>
                    <code className="font-mono text-xs text-green-400">{showPasswords?.[user.user_id] ? user.plain_password : '••••••••'}</code>
                    <button onClick={() => togglePasswordVisibility?.(user.user_id)} className="text-gray-500 hover:text-white"><Eye size={12} /></button>
                    <button onClick={() => copyToClipboard(user.plain_password, 'Password')} className="text-gray-500 hover:text-white"><Copy size={12} /></button>
                  </>
                ) : user.auth_provider === 'google' ? (
                  <span className="text-gray-400 text-xs italic">Google Auth</span>
                ) : (
                  <span className="text-gray-500 text-xs italic">Not stored</span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* PAGES TAB */}
        {detailTab === 'pages' && (
          <div className="space-y-5">
            <div className="flex items-center justify-between">
              <p className="text-gray-400 text-sm">Click pages to toggle visibility for this user. Changes save instantly.</p>
              {savingPages && <span className="text-xs text-blue-400 flex items-center gap-1"><RefreshCw size={12} className="animate-spin" /> Saving...</span>}
            </div>
            {pagesLoading ? (
              <div className="flex items-center justify-center py-10"><RefreshCw className="animate-spin text-gray-400" size={20} /></div>
            ) : (
              PAGE_SECTIONS_LIST.map(section => {
                const sectionPages = ALL_SIDEBAR_PAGES.filter(p => p.section === section);
                const allOn = sectionPages.every(p => p.alwaysOn || isPageOn(p.path));
                return (
                  <div key={section}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-widest">{section}</span>
                      <div className="flex gap-1.5">
                        <button onClick={() => toggleAllSection(section, true)}
                          className="px-2 py-0.5 text-xs bg-green-500/15 text-green-400 hover:bg-green-500/25 rounded border border-green-500/25 transition-colors">
                          Show All
                        </button>
                        <button onClick={() => toggleAllSection(section, false)}
                          className="px-2 py-0.5 text-xs bg-gray-700 text-gray-400 hover:bg-gray-600 rounded border border-gray-600 transition-colors">
                          Hide All
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                      {sectionPages.map(page => {
                        const on = page.alwaysOn || isPageOn(page.path);
                        return (
                          <button
                            key={page.path}
                            onClick={() => !page.alwaysOn && togglePageForUser(page.path)}
                            disabled={page.alwaysOn}
                            data-testid={`admin-page-toggle-${page.path.replace('/', '') || 'dashboard'}`}
                            title={page.alwaysOn ? 'Always on' : (on ? 'Click to hide' : 'Click to show')}
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-all text-left ${
                              on
                                ? 'bg-green-500/15 text-green-400 border border-green-500/30 hover:bg-green-500/25'
                                : 'bg-gray-800/80 text-gray-500 border border-gray-700 hover:border-gray-600'
                            } ${page.alwaysOn ? 'cursor-default opacity-70' : 'cursor-pointer'}`}
                          >
                            <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${on ? 'bg-green-400' : 'bg-gray-600'}`} />
                            <span className="truncate">{page.label}</span>
                            {page.alwaysOn && <Lock size={9} className="ml-auto flex-shrink-0 text-gray-600" />}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* ADD-ONS TAB */}
        {detailTab === 'addons' && (
          <div className="space-y-4">
            <p className="text-gray-400 text-sm">Active add-on subscriptions for this user. Admin can revoke any subscription.</p>
            {pagesLoading ? (
              <div className="flex items-center justify-center py-10"><RefreshCw className="animate-spin text-gray-400" size={20} /></div>
            ) : addons.length === 0 ? (
              <div className="text-center py-10 text-gray-500">
                <Package size={32} className="mx-auto mb-3 opacity-40" />
                <p>No active add-on subscriptions</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {addons.map(addon => (
                  <div key={addon.page_key} className="bg-gray-800 border border-gray-700 rounded-xl p-4">
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="text-white font-semibold text-sm">{addon.label}</h4>
                      <span className="px-1.5 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full border border-green-500/30">Active</span>
                    </div>
                    <div className="space-y-1 text-xs text-gray-400 mb-3">
                      <p>Route: <span className="text-gray-300 font-mono">{addon.route}</span></p>
                      <p>Price: <span className="text-white font-semibold">${addon.price_monthly}/mo</span></p>
                      <p>Since: <span className="text-gray-300">{addon.subscribed_at ? new Date(addon.subscribed_at).toLocaleDateString() : '—'}</span></p>
                      <p>Next billing: <span className="text-gray-300">{addon.next_billing ? new Date(addon.next_billing).toLocaleDateString() : '—'}</span></p>
                    </div>
                    <button
                      onClick={() => revokeAddon(addon.page_key, addon.label)}
                      className="w-full py-1.5 bg-red-600/15 hover:bg-red-600/30 text-red-400 text-xs rounded-lg border border-red-600/25 transition-colors flex items-center justify-center gap-1.5"
                    >
                      <XCircle size={12} /> Revoke Access
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const AddonEditForm = ({ page, onSave, onCancel }) => {
  const [form, setForm] = useState({ ...page });
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-xs text-gray-400 block mb-1">Label</label>
          <input value={form.label} onChange={e => setForm(p => ({...p, label: e.target.value}))} className="w-full px-2 py-1.5 bg-black border border-gray-600 rounded text-white text-xs" />
        </div>
        <div>
          <label className="text-xs text-gray-400 block mb-1">Price/month ($)</label>
          <input type="number" step="0.01" value={form.price_monthly} onChange={e => setForm(p => ({...p, price_monthly: parseFloat(e.target.value)}))} className="w-full px-2 py-1.5 bg-black border border-gray-600 rounded text-white text-xs" />
        </div>
      </div>
      <div>
        <label className="text-xs text-gray-400 block mb-1">Description</label>
        <textarea value={form.description} onChange={e => setForm(p => ({...p, description: e.target.value}))} rows={2} className="w-full px-2 py-1.5 bg-black border border-gray-600 rounded text-white text-xs resize-none" />
      </div>
      <div className="flex items-center gap-2">
        <label className="flex items-center gap-1.5 cursor-pointer">
          <input type="checkbox" checked={form.is_addon} onChange={e => setForm(p => ({...p, is_addon: e.target.checked}))} className="w-3.5 h-3.5 rounded" />
          <span className="text-xs text-gray-300">Is add-on (paid for regular users)</span>
        </label>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-xs text-gray-400 block mb-1">Category</label>
          <select value={form.category} onChange={e => setForm(p => ({...p, category: e.target.value}))} className="w-full px-2 py-1.5 bg-black border border-gray-600 rounded text-white text-xs">
            {['Workspace','Lifestyle','CRM','Files','Team','Marketing','Accounting'].map(c => <option key={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs text-gray-400 block mb-1">Color</label>
          <input type="color" value={form.color} onChange={e => setForm(p => ({...p, color: e.target.value}))} className="w-full h-8 bg-black border border-gray-600 rounded cursor-pointer" />
        </div>
      </div>
      <div className="flex gap-2 pt-1">
        <button onClick={() => onSave(form)} className="flex-1 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs font-medium flex items-center justify-center gap-1"><Save size={12} />Save</button>
        <button onClick={onCancel} className="px-3 py-1.5 bg-gray-800 text-gray-300 rounded text-xs">Cancel</button>
      </div>
    </div>
  );
};

// ── Module-level constants & helpers for TemplateEditForm ─────────────────────
const ALL_TEMPLATE_PAGES = [
  '/', '/communications-re', '/contacts-re', '/calendar', '/tasks',
  '/documents-re', '/projects-re', '/travel', '/food', '/tickets',
  '/health', '/shopping', '/lead-generation', '/clients-customers-re',
  '/properties', '/media-portfolio-re', '/media-library', '/media-factory',
  '/music-releases', '/departments-re', '/employees-re', '/inventory',
  '/marketing-re', '/social-media', '/finances-re', '/investments-re', '/notes'
];

const RolePageToggle = ({ setKey, pages, onToggle }) => (
  <div className="flex flex-wrap gap-1 max-h-20 overflow-y-auto">
    {ALL_TEMPLATE_PAGES.map(p => {
      const active = (pages || []).includes(p);
      return (
        <button key={p} type="button" onClick={() => onToggle(p)}
          className={`px-2 py-0.5 rounded text-xs transition-colors ${active
            ? setKey === 'core_pages' ? 'bg-green-500/25 text-green-400 border border-green-500/40' : 'bg-amber-500/25 text-amber-400 border border-amber-500/40'
            : 'bg-gray-800 text-gray-600 border border-gray-700 hover:text-gray-400'
          }`}>
          {p === '/' ? 'dashboard' : p.replace('/', '')}
        </button>
      );
    })}
  </div>
);

const TemplateEditForm = ({ template, allAddonPages = [], onSave, onCancel }) => {
  const isBusiness = template.category === 'Small Business' || template.category === 'Enterprise';
  const [form, setForm] = useState({ ...template, roles: template.roles || [], sub_companies: template.sub_companies || [] });

  // ── Personal: direct role state ───────────────────────────────────────────
  const [editingRoleIdx, setEditingRoleIdx] = useState(null);
  const [newRole, setNewRole] = useState(null);

  // ── Business: sub-company + role state ───────────────────────────────────
  const [expandedSubIdx, setExpandedSubIdx] = useState(null);
  const [newSubCompany, setNewSubCompany] = useState(null);
  const [editingRoleInSub, setEditingRoleInSub] = useState(null); // { subIdx, roleIdx }
  const [newRoleInSub, setNewRoleInSub] = useState(null); // { subIdx, data }

  const togglePage = (list, setKey, page) => {
    setForm(prev => {
      const arr = prev[setKey] || [];
      return { ...prev, [setKey]: arr.includes(page) ? arr.filter(p => p !== page) : [...arr, page] };
    });
  };

  // ── Personal role helpers ─────────────────────────────────────────────────
  const toggleRolePage = (roleIdx, key, page, isNew) => {
    if (isNew) {
      setNewRole(prev => {
        const arr = prev[key] || [];
        return { ...prev, [key]: arr.includes(page) ? arr.filter(p => p !== page) : [...arr, page] };
      });
    } else {
      setForm(prev => ({
        ...prev,
        roles: prev.roles.map((r, i) => i === roleIdx ? { ...r, [key]: r[key].includes(page) ? r[key].filter(p => p !== page) : [...r[key], page] } : r)
      }));
    }
  };
  const saveNewRole = () => {
    if (!newRole?.name?.trim()) return toast.error('Role name required');
    setForm(prev => ({ ...prev, roles: [...prev.roles, { ...newRole, id: newRole.name.toLowerCase().replace(/\s+/g, '_') + '_' + Date.now() }] }));
    setNewRole(null);
  };
  const updateRoleField = (idx, key, val) => setForm(prev => ({ ...prev, roles: prev.roles.map((r, i) => i === idx ? { ...r, [key]: val } : r) }));

  // ── Business sub-company helpers ──────────────────────────────────────────
  const toggleSubRolePage = (subIdx, roleIdx, key, page, isNew) => {
    if (isNew) {
      setNewRoleInSub(prev => {
        const arr = prev.data[key] || [];
        return { ...prev, data: { ...prev.data, [key]: arr.includes(page) ? arr.filter(p => p !== page) : [...arr, page] } };
      });
    } else {
      setForm(prev => ({
        ...prev,
        sub_companies: prev.sub_companies.map((sc, si) => si !== subIdx ? sc : {
          ...sc, roles: sc.roles.map((r, ri) => ri !== roleIdx ? r : {
            ...r, [key]: r[key].includes(page) ? r[key].filter(p => p !== page) : [...r[key], page]
          })
        })
      }));
    }
  };
  const saveNewSubCompany = () => {
    if (!newSubCompany?.name?.trim()) return toast.error('Company name required');
    const sc = { ...newSubCompany, id: newSubCompany.name.toLowerCase().replace(/\s+/g, '_') + '_' + Date.now(), roles: [] };
    setForm(prev => ({ ...prev, sub_companies: [...prev.sub_companies, sc] }));
    setNewSubCompany(null);
    setExpandedSubIdx(form.sub_companies.length);
  };
  const deleteSubCompany = (idx) => setForm(prev => ({ ...prev, sub_companies: prev.sub_companies.filter((_, i) => i !== idx) }));
  const saveNewRoleInSub = () => {
    if (!newRoleInSub?.data?.name?.trim()) return toast.error('Role name required');
    const role = { ...newRoleInSub.data, id: newRoleInSub.data.name.toLowerCase().replace(/\s+/g, '_') + '_' + Date.now() };
    setForm(prev => ({
      ...prev,
      sub_companies: prev.sub_companies.map((sc, i) => i !== newRoleInSub.subIdx ? sc : { ...sc, roles: [...sc.roles, role] })
    }));
    setNewRoleInSub(null);
  };
  const deleteRoleInSub = (subIdx, roleIdx) => setForm(prev => ({
    ...prev,
    sub_companies: prev.sub_companies.map((sc, si) => si !== subIdx ? sc : { ...sc, roles: sc.roles.filter((_, ri) => ri !== roleIdx) })
  }));
  const updateSubRoleField = (subIdx, roleIdx, key, val) => setForm(prev => ({
    ...prev,
    sub_companies: prev.sub_companies.map((sc, si) => si !== subIdx ? sc : {
      ...sc, roles: sc.roles.map((r, ri) => ri !== roleIdx ? r : { ...r, [key]: val })
    })
  }));

  return (
    <div className="space-y-3">
      {/* Basic fields */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-xs text-gray-400 block mb-1">Name</label>
          <input value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} className="w-full px-2 py-1.5 bg-black border border-gray-600 rounded text-white text-xs" />
        </div>
        <div>
          <label className="text-xs text-gray-400 block mb-1">Color</label>
          <input type="color" value={form.color} onChange={e => setForm(p => ({...p, color: e.target.value}))} className="w-full h-8 bg-black border border-gray-600 rounded cursor-pointer" />
        </div>
      </div>
      <div>
        <label className="text-xs text-gray-400 block mb-1">Description</label>
        <textarea value={form.description} onChange={e => setForm(p => ({...p, description: e.target.value}))} rows={2} className="w-full px-2 py-1.5 bg-black border border-gray-600 rounded text-white text-xs resize-none" />
      </div>

      {/* Core Pages — Personal only */}
      {!isBusiness && (
        <div>
          <label className="text-xs text-gray-400 block mb-2">Core Pages (template defaults)</label>
          <div className="flex flex-wrap gap-1 max-h-28 overflow-y-auto">
            {ALL_TEMPLATE_PAGES.map(p => (
              <button key={p} onClick={() => togglePage(form.core_pages, 'core_pages', p)}
                className={`px-2 py-0.5 rounded text-xs transition-colors ${(form.core_pages || []).includes(p) ? 'bg-green-500/25 text-green-400 border border-green-500/40' : 'bg-gray-800 text-gray-500 border border-gray-700'}`}>
                {p === '/' ? 'dashboard' : p.replace('/', '')}
              </button>
            ))}
          </div>
        </div>
      )}
      {!isBusiness && (
        <div>
          <label className="text-xs text-gray-400 block mb-2">Available Add-on Pages</label>
          <div className="flex flex-wrap gap-1 max-h-20 overflow-y-auto">
            {(allAddonPages.length > 0 ? allAddonPages : [{route: '/travel'}, {route: '/food'}, {route: '/tickets'}, {route: '/shopping'}, {route: '/health'}]).map(addon => (
              <button key={addon.route} onClick={() => togglePage(form.addon_pages, 'addon_pages', addon.route)}
                className={`px-2 py-0.5 rounded text-xs transition-colors ${(form.addon_pages || []).includes(addon.route) ? 'bg-amber-500/25 text-amber-400 border border-amber-500/40' : 'bg-gray-800 text-gray-500 border border-gray-700'}`}>
                {(addon.label || addon.route).replace('/', '')}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── PERSONAL: Direct Roles ───────────────────────────────────────── */}
      {!isBusiness && (
        <div className="border-t border-gray-700 pt-3">
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs text-gray-400 font-semibold uppercase tracking-wider">Roles ({(form.roles || []).length})</label>
            {!newRole && (
              <button type="button" onClick={() => setNewRole({ name: '', description: '', core_pages: [], addon_pages: [] })}
                className="text-xs text-purple-400 hover:text-purple-300 flex items-center gap-1 px-2 py-1 bg-purple-500/10 rounded-lg transition-colors">
                <Plus size={11} /> Add Role
              </button>
            )}
          </div>
          <div className="space-y-2">
            {(form.roles || []).map((role, idx) => (
              <div key={idx} className="bg-black border border-gray-700 rounded-lg overflow-hidden">
                <div className="flex items-center justify-between px-3 py-2 cursor-pointer" onClick={() => setEditingRoleIdx(editingRoleIdx === idx ? null : idx)}>
                  <span className="text-white text-xs font-medium">{role.name || 'Unnamed Role'}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-600 text-xs">{role.core_pages?.length || 0}p</span>
                    <button type="button" onClick={e => { e.stopPropagation(); setForm(prev => ({ ...prev, roles: prev.roles.filter((_, i) => i !== idx) })); }} className="text-red-500/60 hover:text-red-400 transition-colors"><X size={12} /></button>
                  </div>
                </div>
                {editingRoleIdx === idx && (
                  <div className="px-3 pb-3 space-y-2 border-t border-gray-800 pt-2">
                    <input value={role.name} onChange={e => updateRoleField(idx, 'name', e.target.value)} placeholder="Role name" className="w-full px-2 py-1 bg-gray-900 border border-gray-700 rounded text-white text-xs" />
                    <input value={role.description || ''} onChange={e => updateRoleField(idx, 'description', e.target.value)} placeholder="Description" className="w-full px-2 py-1 bg-gray-900 border border-gray-700 rounded text-white text-xs" />
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Core Pages</p>
                      <RolePageToggle setKey="core_pages" pages={role.core_pages} onToggle={p => toggleRolePage(idx, 'core_pages', p, false)} />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Add-on Pages</p>
                      <RolePageToggle setKey="addon_pages" pages={role.addon_pages} onToggle={p => toggleRolePage(idx, 'addon_pages', p, false)} />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
          {newRole && (
            <div className="mt-2 bg-purple-500/5 border border-purple-500/20 rounded-lg p-3 space-y-2">
              <input value={newRole.name} onChange={e => setNewRole(p => ({...p, name: e.target.value}))} placeholder="Role name *" className="w-full px-2 py-1 bg-black border border-gray-600 rounded text-white text-xs" />
              <input value={newRole.description} onChange={e => setNewRole(p => ({...p, description: e.target.value}))} placeholder="Description" className="w-full px-2 py-1 bg-black border border-gray-600 rounded text-white text-xs" />
              <div>
                <p className="text-xs text-gray-500 mb-1">Core Pages</p>
                <RolePageToggle setKey="core_pages" pages={newRole.core_pages} onToggle={p => toggleRolePage(null, 'core_pages', p, true)} />
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Add-on Pages</p>
                <RolePageToggle setKey="addon_pages" pages={newRole.addon_pages} onToggle={p => toggleRolePage(null, 'addon_pages', p, true)} />
              </div>
              <div className="flex gap-2 pt-1">
                <button type="button" onClick={saveNewRole} className="flex items-center gap-1 px-3 py-1 bg-purple-600 hover:bg-purple-700 text-white rounded text-xs"><Check size={11} /> Add Role</button>
                <button type="button" onClick={() => setNewRole(null)} className="px-3 py-1 bg-gray-800 text-gray-300 rounded text-xs">Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── BUSINESS: Sub-Companies → Roles ──────────────────────────────── */}
      {isBusiness && (
        <div className="border-t border-gray-700 pt-3">
          <div className="flex items-center justify-between mb-3">
            <label className="text-xs text-gray-400 font-semibold uppercase tracking-wider">Sub-Companies ({(form.sub_companies || []).length})</label>
            {!newSubCompany && (
              <button type="button" onClick={() => setNewSubCompany({ name: '', description: '' })}
                className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1 px-2 py-1 bg-blue-500/10 rounded-lg transition-colors">
                <Plus size={11} /> Add Company
              </button>
            )}
          </div>

          {/* New sub-company form */}
          {newSubCompany && (
            <div className="mb-3 bg-blue-500/5 border border-blue-500/20 rounded-lg p-3 space-y-2">
              <input value={newSubCompany.name} onChange={e => setNewSubCompany(p => ({...p, name: e.target.value}))} placeholder="Company name *" className="w-full px-2 py-1 bg-black border border-gray-600 rounded text-white text-xs" />
              <input value={newSubCompany.description} onChange={e => setNewSubCompany(p => ({...p, description: e.target.value}))} placeholder="Description" className="w-full px-2 py-1 bg-black border border-gray-600 rounded text-white text-xs" />
              <div className="flex gap-2">
                <button type="button" onClick={saveNewSubCompany} className="flex items-center gap-1 px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs"><Check size={11} /> Add</button>
                <button type="button" onClick={() => setNewSubCompany(null)} className="px-3 py-1 bg-gray-800 text-gray-300 rounded text-xs">Cancel</button>
              </div>
            </div>
          )}

          {/* Sub-company list */}
          <div className="space-y-2">
            {(form.sub_companies || []).map((sc, si) => (
              <div key={si} className="bg-black border border-gray-700 rounded-xl overflow-hidden">
                {/* Sub-company header */}
                <div className="flex items-center gap-2 px-3 py-2.5 cursor-pointer" onClick={() => setExpandedSubIdx(expandedSubIdx === si ? null : si)}>
                  {expandedSubIdx === si ? <ChevronDown size={13} className="text-gray-400" /> : <ChevronRight size={13} className="text-gray-400" />}
                  <span className="text-white text-xs font-semibold flex-1">{sc.name}</span>
                  <span className="text-gray-600 text-xs">{sc.roles?.length || 0} roles</span>
                  <button type="button" onClick={e => { e.stopPropagation(); deleteSubCompany(si); }} className="text-red-500/60 hover:text-red-400 ml-1"><X size={12} /></button>
                </div>

                {/* Expanded sub-company content */}
                {expandedSubIdx === si && (
                  <div className="border-t border-gray-800 px-3 pb-3 pt-2 space-y-2">
                    <input value={sc.name} onChange={e => setForm(prev => ({ ...prev, sub_companies: prev.sub_companies.map((s, i) => i === si ? {...s, name: e.target.value} : s) }))} placeholder="Company name" className="w-full px-2 py-1 bg-gray-900 border border-gray-700 rounded text-white text-xs" />
                    <input value={sc.description || ''} onChange={e => setForm(prev => ({ ...prev, sub_companies: prev.sub_companies.map((s, i) => i === si ? {...s, description: e.target.value} : s) }))} placeholder="Description" className="w-full px-2 py-1 bg-gray-900 border border-gray-700 rounded text-white text-xs" />

                    {/* Roles within this sub-company */}
                    <div className="pt-1">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-xs text-gray-500 uppercase tracking-wider">Roles ({sc.roles?.length || 0})</span>
                        {!(newRoleInSub?.subIdx === si) && (
                          <button type="button" onClick={() => setNewRoleInSub({ subIdx: si, data: { name: '', description: '', core_pages: [], addon_pages: [] } })}
                            className="text-xs text-purple-400 flex items-center gap-1 px-1.5 py-0.5 bg-purple-500/10 rounded transition-colors hover:text-purple-300">
                            <Plus size={10} /> Add Role
                          </button>
                        )}
                      </div>

                      {/* Existing roles */}
                      <div className="space-y-1.5">
                        {(sc.roles || []).map((role, ri) => {
                          const rKey = `${si}-${ri}`;
                          const isEditing = editingRoleInSub?.subIdx === si && editingRoleInSub?.roleIdx === ri;
                          return (
                            <div key={ri} className="bg-gray-900 border border-gray-700 rounded-lg overflow-hidden">
                              <div className="flex items-center justify-between px-2.5 py-1.5 cursor-pointer" onClick={() => setEditingRoleInSub(isEditing ? null : { subIdx: si, roleIdx: ri })}>
                                <span className="text-gray-200 text-xs">{role.name || 'Unnamed'}</span>
                                <div className="flex items-center gap-2">
                                  <span className="text-gray-600 text-xs">{role.core_pages?.length || 0}p</span>
                                  <button type="button" onClick={e => { e.stopPropagation(); deleteRoleInSub(si, ri); }} className="text-red-500/60 hover:text-red-400"><X size={11} /></button>
                                </div>
                              </div>
                              {isEditing && (
                                <div className="px-2.5 pb-2.5 border-t border-gray-800 pt-2 space-y-1.5">
                                  <input value={role.name} onChange={e => updateSubRoleField(si, ri, 'name', e.target.value)} placeholder="Role name" className="w-full px-2 py-1 bg-black border border-gray-700 rounded text-white text-xs" />
                                  <input value={role.description || ''} onChange={e => updateSubRoleField(si, ri, 'description', e.target.value)} placeholder="Description" className="w-full px-2 py-1 bg-black border border-gray-700 rounded text-white text-xs" />
                                  <div>
                                    <p className="text-xs text-gray-600 mb-1">Core Pages</p>
                                    <RolePageToggle setKey="core_pages" pages={role.core_pages} onToggle={p => toggleSubRolePage(si, ri, 'core_pages', p, false)} />
                                  </div>
                                  <div>
                                    <p className="text-xs text-gray-600 mb-1">Add-on Pages</p>
                                    <RolePageToggle setKey="addon_pages" pages={role.addon_pages} onToggle={p => toggleSubRolePage(si, ri, 'addon_pages', p, false)} />
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      {/* New role form for this sub-company */}
                      {newRoleInSub?.subIdx === si && (
                        <div className="mt-2 bg-purple-500/5 border border-purple-500/20 rounded-lg p-2.5 space-y-1.5">
                          <input value={newRoleInSub.data.name} onChange={e => setNewRoleInSub(p => ({...p, data: {...p.data, name: e.target.value}}))} placeholder="Role name *" className="w-full px-2 py-1 bg-black border border-gray-600 rounded text-white text-xs" />
                          <input value={newRoleInSub.data.description} onChange={e => setNewRoleInSub(p => ({...p, data: {...p.data, description: e.target.value}}))} placeholder="Description" className="w-full px-2 py-1 bg-black border border-gray-600 rounded text-white text-xs" />
                          <div>
                            <p className="text-xs text-gray-600 mb-1">Core Pages</p>
                            <RolePageToggle setKey="core_pages" pages={newRoleInSub.data.core_pages} onToggle={p => toggleSubRolePage(si, null, 'core_pages', p, true)} />
                          </div>
                          <div>
                            <p className="text-xs text-gray-600 mb-1">Add-on Pages</p>
                            <RolePageToggle setKey="addon_pages" pages={newRoleInSub.data.addon_pages} onToggle={p => toggleSubRolePage(si, null, 'addon_pages', p, true)} />
                          </div>
                          <div className="flex gap-2 pt-1">
                            <button type="button" onClick={saveNewRoleInSub} className="flex items-center gap-1 px-3 py-1 bg-purple-600 text-white rounded text-xs"><Check size={10} /> Save</button>
                            <button type="button" onClick={() => setNewRoleInSub(null)} className="px-3 py-1 bg-gray-800 text-gray-300 rounded text-xs">Cancel</button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Save / Cancel */}
      <div className="flex gap-2 pt-2 border-t border-gray-700">
        <button onClick={() => onSave(form)} className="flex-1 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded text-xs font-medium flex items-center justify-center gap-1"><Save size={12} /> Save</button>
        <button onClick={onCancel} className="px-3 py-1.5 bg-gray-800 text-gray-300 rounded text-xs">Cancel</button>
      </div>
    </div>
  );
};
export default AdminDashboard;
